import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useReviewMode } from "../context/ReviewModeContext";
import { callApi } from "../api/api";
import {
  ArrowLeft,
  RefreshCw,
  TrendingUp,
  Users,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ExternalLink,
  PieChart,
  BarChart2,
  BarChart3,
  Target,
  Filter,
  ChevronDown,
  Check,
  Building2,
  FolderGit2,
  GitBranch,
  Tag,
  User,
  Award,
  Loader2,
  X,
  Search,
  FileText,
  Calendar,
  ArrowUpRight,
  LayoutDashboard,
  Layers
} from "lucide-react";
import "./InboxReview.css";
import MetadataReview from "./InboxReview";
import PerformanceActivityPage from "./PerformanceActivity/PerformanceActivityPage";
import LoadingOverlay from "../components/LoadingOverlay";
import { departmentTone, namedDepartments } from "./inboxReviewChrome";
import { formatInboxCount } from "./inboxSummaryKpis";

function MetricSkel({ variant = "num" }) {
  return (
    <span
      className={`ir-skel ir-skel--${variant}`}
      aria-hidden="true"
    />
  );
}

/* ─────────────────────────────────────────────────────────────
   ENHANCED SVG TREND LINE & DUAL BAR CHART COMPONENT
   ───────────────────────────────────────────────────────────── */
function SVGVelocityChart({ data = [] }) {
  if (!data || data.length === 0) {
    return <div style={{ textAlign: "center", color: "#94a3b8", padding: "24px", fontSize: "13px" }}>No velocity trend data recorded for selected period.</div>;
  }

  const width = 560;
  const height = 230;
  const paddingLeft = 45;
  const paddingRight = 25;
  const paddingTop = 30;
  const paddingBottom = 40;

  const maxVal = Math.max(...data.map((d) => Math.max(d.created || 0, d.completed || 0)), 4);

  const pointsCreated = data.map((d, i) => {
    const x = paddingLeft + (i * (width - paddingLeft - paddingRight)) / Math.max(data.length - 1, 1);
    const y = height - paddingBottom - ((d.created || 0) / maxVal) * (height - paddingTop - paddingBottom);
    return { x, y, val: d.created, period: d.period };
  });

  const pointsCompleted = data.map((d, i) => {
    const x = paddingLeft + (i * (width - paddingLeft - paddingRight)) / Math.max(data.length - 1, 1);
    const y = height - paddingBottom - ((d.completed || 0) / maxVal) * (height - paddingTop - paddingBottom);
    return { x, y, val: d.completed, period: d.period };
  });

  // Helper for smooth Bezier curve
  const getSmoothPath = (pts) => {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const curr = pts[i];
      const next = pts[i + 1];
      const cp1x = curr.x + (next.x - curr.x) / 2;
      const cp1y = curr.y;
      const cp2x = curr.x + (next.x - curr.x) / 2;
      const cp2y = next.y;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
    }
    return d;
  };

  const pathCreated = getSmoothPath(pointsCreated);
  const pathCompleted = getSmoothPath(pointsCompleted);

  const areaCreated = `${pathCreated} L ${pointsCreated[pointsCreated.length - 1].x} ${height - paddingBottom} L ${pointsCreated[0].x} ${height - paddingBottom} Z`;
  const areaCompleted = `${pathCompleted} L ${pointsCompleted[pointsCompleted.length - 1].x} ${height - paddingBottom} L ${pointsCompleted[0].x} ${height - paddingBottom} Z`;

  const totalCreated = data.reduce((acc, d) => acc + (d.created || 0), 0);
  const totalCompleted = data.reduce((acc, d) => acc + (d.completed || 0), 0);
  const hasCreatedData = data.some((d) => (d.created || 0) > 0);
  const hasCompletedData = data.some((d) => (d.completed || 0) > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
      {/* Velocity Summary Strip */}
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "4px" }}>
        <div style={{ background: "rgba(37, 99, 235, 0.06)", padding: "6px 12px", borderRadius: "6px", border: "1px solid rgba(37, 99, 235, 0.15)", fontSize: "11px", fontWeight: "700", color: "#1d4ed8" }}>
          🚀 Total Created: {totalCreated}
        </div>
        <div style={{ background: "rgba(16, 185, 129, 0.06)", padding: "6px 12px", borderRadius: "6px", border: "1px solid rgba(16, 185, 129, 0.15)", fontSize: "11px", fontWeight: "700", color: "#047857" }}>
          ✅ Total Completed: {totalCompleted}
        </div>
        <div style={{ background: "#f8fafc", padding: "6px 12px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "11px", fontWeight: "700", color: "#475569" }}>
          ⚡ Peak Velocity: {maxVal} / Period
        </div>
      </div>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", minWidth: "350px" }}>
          <defs>
            <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="gradCompleted" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Grid lines & Y-Axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
            const y = height - paddingBottom - ratio * (height - paddingTop - paddingBottom);
            const valLabel = Math.round(ratio * maxVal);
            return (
              <g key={idx}>
                <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#f1f5f9" strokeDasharray="3 3" />
                <text x={paddingLeft - 10} y={y + 4} fontSize="10" fontWeight="700" fill="#94a3b8" textAnchor="end">
                  {valLabel}
                </text>
              </g>
            );
          })}

          {/* Bezier Area Paths */}
          {hasCreatedData && <path d={areaCreated} fill="url(#gradCreated)" />}
          {hasCompletedData && <path d={areaCompleted} fill="url(#gradCompleted)" />}

          {/* Bezier Line Paths */}
          {hasCreatedData && <path d={pathCreated} fill="none" stroke="#2563eb" strokeWidth="3.5" strokeLinecap="round" />}
          {hasCompletedData && <path d={pathCompleted} fill="none" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" />}

          {/* Interactive Data Points & Value Callout Badges */}
          {pointsCreated.map((p, i) => (
            <g key={`c-${i}`}>
              {p.val > 0 && (
                <>
                  <circle cx={p.x} cy={p.y} r="5" fill="#2563eb" stroke="#ffffff" strokeWidth="2.5" />
                  <g>
                    <rect x={p.x - 14} y={p.y - 22} width="28" height="15" rx="4" fill="#2563eb" />
                    <text x={p.x} y={p.y - 11} fontSize="10" fontWeight="800" fill="#ffffff" textAnchor="middle">
                      +{p.val}
                    </text>
                  </g>
                </>
              )}
              <text x={p.x} y={height - 12} fontSize="11" fontWeight="700" fill="#64748b" textAnchor="middle">
                {p.period}
              </text>
            </g>
          ))}

          {pointsCompleted.map((p, i) => (
            <g key={`cm-${i}`}>
              {p.val > 0 && (
                <>
                  <circle cx={p.x} cy={p.y} r="5" fill="#10b981" stroke="#ffffff" strokeWidth="2.5" />
                  <g>
                    <rect x={p.x - 14} y={p.y - 22} width="28" height="15" rx="4" fill="#10b981" />
                    <text x={p.x} y={p.y - 11} fontSize="10" fontWeight="800" fill="#ffffff" textAnchor="middle">
                      {p.val}
                    </text>
                  </g>
                </>
              )}
            </g>
          ))}
        </svg>
      </div>

      {/* Velocity Insights & Sprint Health Grid (Fills bottom gap meaningfully) */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: "10px",
        marginTop: "10px",
        padding: "12px 14px",
        background: "#f8fafc",
        borderRadius: "10px",
        border: "1px solid #e2e8f0"
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Intake Rate</span>
          <span style={{ fontSize: "14px", fontWeight: "800", color: "#0f172a" }}>
            {(totalCreated / Math.max(data.length, 1)).toFixed(1)} <span style={{ fontSize: "10px", fontWeight: "600", color: "#64748b" }}>/ period</span>
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Net Backlog</span>
          <span style={{ fontSize: "14px", fontWeight: "800", color: totalCreated - totalCompleted > 0 ? "#2563eb" : "#10b981" }}>
            {totalCreated - totalCompleted >= 0 ? `+${totalCreated - totalCompleted}` : totalCreated - totalCompleted} items
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Resolution Ratio</span>
          <span style={{ fontSize: "14px", fontWeight: "800", color: totalCreated > 0 ? (totalCompleted / totalCreated >= 0.8 ? "#10b981" : "#d97706") : "#64748b" }}>
            {totalCreated > 0 ? Math.round((totalCompleted / totalCreated) * 100) : 0}%
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Flow Status</span>
          <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
            {totalCompleted >= totalCreated && totalCreated > 0 ? (
              <span style={{ color: "#16a34a" }}>🟢 Target Reached</span>
            ) : totalCompleted > 0 ? (
              <span style={{ color: "#d97706" }}>🟡 Active Progress</span>
            ) : (
              <span style={{ color: "#2563eb" }}>🔵 Backlog Generation</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SVG WORK ITEM TYPE MULTI-LINE GRAPH CHART COMPONENT
   Each work item type gets its own smooth colored bezier line
   plotted over time periods from velocityTrend data.
   ───────────────────────────────────────────────────────────── */
const TYPE_COLORS = {
  "User Story": "#10b981",
  "Task": "#3b82f6",
  "Bug": "#ef4444",
  "Issue": "#f59e0b",
  "Requirement": "#8b5cf6",
  "Feature": "#ec4899",
  "Epic": "#0ea5e9",
  "Other": "#64748b",
};
function getTypeColor(type) {
  for (const [key, color] of Object.entries(TYPE_COLORS)) {
    if (type.toLowerCase().includes(key.toLowerCase())) return color;
  }
  // Deterministic fallback color from a palette
  const palette = ["#10b981", "#3b82f6", "#ef4444", "#f59e0b", "#8b5cf6", "#ec4899", "#0ea5e9", "#14b8a6", "#f97316"];
  let hash = 0;
  for (let c of type) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return palette[Math.abs(hash) % palette.length];
}

function SVGWorkItemTypeLineChart({ velocityTrend = [], typeCounts = {} }) {
  // Collect all known types from typeCounts + trend data
  const typeSet = new Set(Object.keys(typeCounts));
  velocityTrend.forEach((d) => {
    if (d.types) Object.keys(d.types).forEach((t) => typeSet.add(t));
  });
  const types = Array.from(typeSet).filter((t) => t && t !== "");

  const periods = velocityTrend.map((d) => d.period);

  if (types.length === 0 || periods.length === 0) {
    return <div style={{ textAlign: "center", color: "#94a3b8", padding: "24px", fontSize: "13px" }}>No work item type trend data available for the selected period.</div>;
  }

  const width = 680;
  const height = 240;
  const paddingLeft = 48;
  const paddingRight = 30;
  const paddingTop = 20;
  const paddingBottom = 40;
  const chartW = width - paddingLeft - paddingRight;
  const chartH = height - paddingTop - paddingBottom;

  // Max value across all types all periods
  const maxVal = Math.max(
    ...velocityTrend.map((d) => Math.max(...types.map((t) => (d.types && d.types[t]) || 0), 0)),
    4
  );

  // X positions for each period
  const xOf = (i) => paddingLeft + (periods.length <= 1 ? chartW / 2 : (i * chartW) / (periods.length - 1));
  // Y position for a value
  const yOf = (val) => height - paddingBottom - (val / maxVal) * chartH;

  // Smooth bezier path
  const smoothPath = (pts) => {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const cur = pts[i], nxt = pts[i + 1];
      const cpx = cur.x + (nxt.x - cur.x) / 2;
      d += ` C ${cpx} ${cur.y}, ${cpx} ${nxt.y}, ${nxt.x} ${nxt.y}`;
    }
    return d;
  };

  return (
    <div style={{ width: "100%", marginTop: "12px" }}>
      {/* Color Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "10px", paddingLeft: "8px" }}>
        {types.map((type) => {
          const color = getTypeColor(type);
          const total = typeCounts[type] || 0;
          return (
            <div key={type} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ display: "inline-block", width: "20px", height: "3px", background: color, borderRadius: "2px" }} />
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>{type}</span>
              {total > 0 && (
                <span style={{ fontSize: "11px", fontWeight: "600", color: "#94a3b8" }}>({total})</span>
              )}
            </div>
          );
        })}
      </div>

      {/* SVG Chart */}
      <div style={{ width: "100%", overflowX: "auto" }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", minWidth: "420px" }}>
          <defs>
            {types.map((type) => {
              const color = getTypeColor(type);
              const safeId = `typeLineArea_${type.replace(/\s+/g, "_")}`;
              return (
                <linearGradient key={safeId} id={safeId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={color} stopOpacity="0.01" />
                </linearGradient>
              );
            })}
          </defs>

          {/* Horizontal Grid lines & Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
            const y = height - paddingBottom - ratio * chartH;
            return (
              <g key={idx}>
                <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#f1f5f9" strokeDasharray="4 3" />
                <text x={paddingLeft - 8} y={y + 4} fontSize="10" fontWeight="700" fill="#cbd5e1" textAnchor="end">
                  {Math.round(ratio * maxVal)}
                </text>
              </g>
            );
          })}

          {/* X-axis period labels */}
          {periods.map((p, i) => (
            <text key={i} x={xOf(i)} y={height - 10} fontSize="10" fontWeight="700" fill="#64748b" textAnchor="middle">
              {p}
            </text>
          ))}

          {/* Draw area fill + line + dots per type */}
          {types.map((type) => {
            const color = getTypeColor(type);
            const safeAreaId = `typeLineArea_${type.replace(/\s+/g, "_")}`;
            const pts = velocityTrend.map((d, i) => ({
              x: xOf(i),
              y: yOf((d.types && d.types[type]) || 0),
              val: (d.types && d.types[type]) || 0,
            }));
            const linePath = smoothPath(pts);
            const baseline = height - paddingBottom;
            const areaPath = pts.length > 0
              ? `${linePath} L ${pts[pts.length - 1].x} ${baseline} L ${pts[0].x} ${baseline} Z`
              : "";

            return (
              <g key={type}>
                {/* Area fill */}
                {areaPath && <path d={areaPath} fill={`url(#${safeAreaId})`} />}
                {/* Line */}
                <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {/* Dots + badges */}
                {pts.map((p, i) => (
                  <g key={i}>
                    {p.val > 0 && (
                      <>
                        <circle cx={p.x} cy={p.y} r="4.5" fill={color} stroke="#ffffff" strokeWidth="2" />
                        <g>
                          <rect x={p.x - 16} y={p.y - 22} width="32" height="14" rx="4" fill={color} opacity="0.92" />
                          <text x={p.x} y={p.y - 12} fontSize="9" fontWeight="800" fill="#ffffff" textAnchor="middle">
                            {p.val}
                          </text>
                        </g>
                      </>
                    )}
                    {p.val === 0 && (
                      <circle cx={p.x} cy={p.y} r="2.5" fill={color} opacity="0.3" />
                    )}
                  </g>
                ))}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SVG ETA MISSED vs ON-TIME TREND LINE CHART
   Two smooth bezier lines: red=missed, green=on-time
   ───────────────────────────────────────────────────────────── */
function SVGEtaTrendChart({ etaTrend = [] }) {
  if (!etaTrend || etaTrend.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "#94a3b8", padding: "24px", fontSize: "13px" }}>
        No ETA trend data available for the selected period.
      </div>
    );
  }

  const width = 680;
  const height = 220;
  const pL = 48, pR = 30, pT = 28, pB = 38;
  const chartW = width - pL - pR;
  const chartH = height - pT - pB;

  const maxVal = Math.max(...etaTrend.map((d) => Math.max(d.missed || 0, d.onTime || 0)), 4);
  const xOf = (i) => pL + (etaTrend.length <= 1 ? chartW / 2 : (i * chartW) / (etaTrend.length - 1));
  const yOf = (val) => height - pB - (val / maxVal) * chartH;

  const smoothPath = (pts) => {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const cur = pts[i], nxt = pts[i + 1];
      const cpx = cur.x + (nxt.x - cur.x) / 2;
      d += ` C ${cpx} ${cur.y}, ${cpx} ${nxt.y}, ${nxt.x} ${nxt.y}`;
    }
    return d;
  };

  const missedPts = etaTrend.map((d, i) => ({ x: xOf(i), y: yOf(d.missed || 0), val: d.missed || 0 }));
  const onTimePts = etaTrend.map((d, i) => ({ x: xOf(i), y: yOf(d.onTime || 0), val: d.onTime || 0 }));

  const missedLine = smoothPath(missedPts);
  const onTimeLine = smoothPath(onTimePts);
  const baseline = height - pB;

  const missedArea = missedPts.length > 0
    ? `${missedLine} L ${missedPts[missedPts.length - 1].x} ${baseline} L ${missedPts[0].x} ${baseline} Z` : "";
  const onTimeArea = onTimePts.length > 0
    ? `${onTimeLine} L ${onTimePts[onTimePts.length - 1].x} ${baseline} L ${onTimePts[0].x} ${baseline} Z` : "";

  const totalMissed = etaTrend.reduce((s, d) => s + (d.missed || 0), 0);
  const totalOnTime = etaTrend.reduce((s, d) => s + (d.onTime || 0), 0);

  return (
    <div style={{ width: "100%", marginTop: "4px" }}>
      {/* Legend */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "10px", paddingLeft: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <span style={{ display: "inline-block", width: "22px", height: "3px", background: "#ef4444", borderRadius: "2px" }} />
          <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>ETA Missed</span>
          <span style={{ fontSize: "11px", fontWeight: "700", padding: "1px 7px", borderRadius: "5px", background: "#fee2e2", color: "#dc2626" }}>{totalMissed}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <span style={{ display: "inline-block", width: "22px", height: "3px", background: "#10b981", borderRadius: "2px" }} />
          <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>On Time</span>
          <span style={{ fontSize: "11px", fontWeight: "700", padding: "1px 7px", borderRadius: "5px", background: "#d1fae5", color: "#065f46" }}>{totalOnTime}</span>
        </div>
      </div>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", minWidth: "400px" }}>
          <defs>
            <linearGradient id="etaMissedGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0.01" />
            </linearGradient>
            <linearGradient id="etaOnTimeGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {/* Y-axis grid + labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
            const y = height - pB - ratio * chartH;
            return (
              <g key={idx}>
                <line x1={pL} y1={y} x2={width - pR} y2={y} stroke="#f1f5f9" strokeDasharray="4 3" />
                <text x={pL - 8} y={y + 4} fontSize="10" fontWeight="700" fill="#cbd5e1" textAnchor="end">
                  {Math.round(ratio * maxVal)}
                </text>
              </g>
            );
          })}

          {/* X-axis period labels */}
          {etaTrend.map((d, i) => (
            <text key={i} x={xOf(i)} y={height - 8} fontSize="10" fontWeight="700" fill="#64748b" textAnchor="middle">
              {d.period}
            </text>
          ))}

          {/* On-Time area + line */}
          {onTimeArea && <path d={onTimeArea} fill="url(#etaOnTimeGrad)" />}
          <path d={onTimeLine} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Missed area + line (on top) */}
          {missedArea && <path d={missedArea} fill="url(#etaMissedGrad)" />}
          <path d={missedLine} fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* On-Time dots + badges */}
          {onTimePts.map((p, i) => (
            <g key={`ot-${i}`}>
              <circle cx={p.x} cy={p.y} r={p.val > 0 ? 4.5 : 2.5} fill="#10b981" stroke="#fff" strokeWidth="2" opacity={p.val > 0 ? 1 : 0.3} />
              {p.val > 0 && (
                <g>
                  <rect x={p.x - 14} y={p.y - 22} width="28" height="14" rx="4" fill="#10b981" opacity="0.92" />
                  <text x={p.x} y={p.y - 12} fontSize="9" fontWeight="800" fill="#fff" textAnchor="middle">{p.val}</text>
                </g>
              )}
            </g>
          ))}

          {/* Missed dots + badges */}
          {missedPts.map((p, i) => (
            <g key={`ms-${i}`}>
              <circle cx={p.x} cy={p.y} r={p.val > 0 ? 4.5 : 2.5} fill="#ef4444" stroke="#fff" strokeWidth="2" opacity={p.val > 0 ? 1 : 0.3} />
              {p.val > 0 && (
                <g>
                  <rect x={p.x - 14} y={p.y - 22} width="28" height="14" rx="4" fill="#ef4444" opacity="0.92" />
                  <text x={p.x} y={p.y - 12} fontSize="9" fontWeight="800" fill="#fff" textAnchor="middle">{p.val}</text>
                </g>
              )}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   CUSTOM MODERN FLOATING POP-OVER DROPDOWN COMPONENT
   ───────────────────────────────────────────────────────────── */
function CustomDropdown({ label, value, options, onChange, icon: Icon, disabled, disabledHint, loading }) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = React.useRef(null);
  const optionRefs = React.useRef([]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [isOpen, focusedIndex]);

  const selectedOpt = options.find((o) => o.value === value) || { label: value || "Select", value };

  const handleKeyDown = (e) => {
    if (disabled || loading) return;

    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setIsOpen(true);
        const idx = options.findIndex((o) => o.value === value);
        setFocusedIndex(idx >= 0 ? idx : 0);
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < options.length) {
        onChange(options[focusedIndex].value);
        setIsOpen(false);
      }
    } else if (e.key === "Tab") {
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        title={disabled ? (disabledHint || "Disabled") : ""}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 12px",
          background: disabled || loading ? "#f8fafc" : "#f1f5f9",
          border: disabled ? "1px solid #e2e8f0" : isOpen ? "1px solid #2563eb" : "1px solid #cbd5e1",
          borderRadius: "8px",
          cursor: disabled || loading ? "not-allowed" : "pointer",
          fontSize: "12px",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: isOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
          opacity: disabled ? 0.6 : 1,
          outline: "none"
        }}
      >
        {Icon && <Icon size={13} color={disabled ? "#94a3b8" : "#64748b"} />}
        <span style={{ color: disabled ? "#94a3b8" : "#64748b", fontWeight: "600" }}>{label}:</span>
        <span style={{ color: disabled ? "#94a3b8" : "#0f172a", fontWeight: "700" }}>
          {selectedOpt.label}
        </span>
        {loading ? (
          <svg width="13" height="13" viewBox="0 0 14 14" style={{ animation: "trackingSpinAnim 0.8s linear infinite", flexShrink: 0 }}>
            <circle cx="7" cy="7" r="5.5" fill="none" stroke="#e2e8f0" strokeWidth="2" />
            <path d="M 7 1.5 A 5.5 5.5 0 0 1 12.5 7" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <ChevronDown size={14} color={disabled ? "#cbd5e1" : "#64748b"} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)" }} />
        )}
      </button>

      {isOpen && !disabled && !loading && (
        <div
          role="listbox"
          tabIndex={-1}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            width: "100%",
            minWidth: "100%",
            maxHeight: "260px",
            overflowY: "auto",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 8px 32px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)",
            padding: "6px",
            zIndex: 9999,
            animation: "dropdownFadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1)",
            outline: "none"
          }}
        >
          {options.map((opt, idx) => {
            const isSelected = opt.value === value;
            const isFocused = idx === focusedIndex;
            return (
              <button
                key={opt.value}
                ref={(el) => (optionRefs.current[idx] = el)}
                type="button"
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                onMouseEnter={() => setFocusedIndex(idx)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                  padding: "9px 10px",
                  borderRadius: "7px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  textAlign: "left",
                  boxSizing: "border-box",
                  transition: "background 0.1s",
                  background: isSelected ? "rgba(37,99,235,0.08)" : "transparent",
                  outline: isFocused ? "1px solid rgb(37,99,235)" : "none",
                  outlineOffset: "-1px",
                  color: (isSelected || isFocused) ? "rgb(37,99,235)" : "#0f172a",
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {opt.label}
                </span>
                {isSelected && (
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M5 10l4 4 6-8" stroke="rgb(37,99,235)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   CIRCULAR PROGRESS SLA COMPONENT
   ───────────────────────────────────────────────────────────── */
function SVGCircularProgress({ percent = 100 }) {
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div style={{ position: "relative", width: "64px", height: "64px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="64" height="64" viewBox="0 0 60 60" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="30" cy="30" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle
          cx="30"
          cy="30"
          r={radius}
          fill="none"
          stroke={percent >= 90 ? "#10b981" : percent >= 75 ? "#f59e0b" : "#ef4444"}
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SVG DONUT CHART COMPONENT
   ───────────────────────────────────────────────────────────── */
function SVGDonutChart({ data = [] }) {
  const total = data.reduce((acc, d) => acc + d.count, 0);
  if (total === 0) {
    return <div style={{ textAlign: "center", color: "#94a3b8", padding: "16px", fontSize: "12px", fontStyle: "italic" }}>No data to display</div>;
  }

  let accumulatedPercent = 0;

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", flexWrap: "wrap", margin: "12px 0" }}>
      <svg width="130" height="130" viewBox="0 0 42 42" style={{ flexShrink: 0, transform: "rotate(-90deg)" }}>
        <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#f1f5f9" strokeWidth="6" />
        {data.map((d, i) => {
          const percent = (d.count / total) * 100;
          const strokeDasharray = `${percent} ${100 - percent}`;
          const strokeDashoffset = 100 - accumulatedPercent + 25;
          accumulatedPercent += percent;

          return (
            <circle
              key={i}
              cx="21"
              cy="21"
              r="15.91549430918954"
              fill="transparent"
              stroke={d.color}
              strokeWidth="6"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              style={{ transition: "stroke-dasharray 0.5s ease" }}
            />
          );
        })}
      </svg>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: d.color, display: "inline-block" }} />
              <span style={{ fontWeight: "600", color: "#334155" }}>{d.label}</span>
            </div>
            <span style={{ fontWeight: "800", color: "#0f172a" }}>{d.count} ({Math.round((d.count / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SVG RADIAL PERCENTAGE RING COMPONENT
   ───────────────────────────────────────────────────────────── */
function SVGRadialGauge({ percent = 0, size = 44, strokeWidth = 8, color = "#10b981" }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div style={{ position: "relative", width: `${size}px`, height: `${size}px`, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke="#f1f5f9" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <span style={{ position: "absolute", fontSize: "10px", fontWeight: "800", color: "#0f172a" }}>
        {percent}%
      </span>
    </div>
  );
}

const THEME = "rgb(37, 99, 235)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";

function isDevOpsAnalyticsSection(section) {
  return !["metadata", "departments", "user-activity"].includes(section);
}

const OVERVIEW_LOOKBACKS = [
  { id: "30d", label: "Last 30 days" },
  { id: "custom", label: "Date range" },
];

function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function defaultCustomRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 30);
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
}

function rangeStartIso(dateStr) {
  if (!dateStr) return "";
  const from = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(from.getTime()) ? "" : from.toISOString();
}

function rangeEndIso(dateStr) {
  if (!dateStr) return "";
  const to = new Date(`${dateStr}T23:59:59.999`);
  return Number.isNaN(to.getTime()) ? "" : to.toISOString();
}

function getOverviewLookbackRange(lookback, customFrom = "", customTo = "") {
  if (lookback === "custom") {
    let from_date = rangeStartIso(customFrom);
    let to_date = rangeEndIso(customTo);
    if (from_date && to_date && from_date > to_date) {
      from_date = rangeStartIso(customTo);
      to_date = rangeEndIso(customFrom);
    }
    return { from_date, to_date };
  }

  const now = new Date();
  const from = new Date(now);
  if (lookback === "today") {
    from.setHours(0, 0, 0, 0);
  } else {
    from.setDate(now.getDate() - 30);
  }

  return {
    from_date: from.toISOString(),
    to_date: now.toISOString(),
  };
}

function getLookbackHint(lookback, customFrom = "", customTo = "") {
  if (lookback === "today") return "Today";
  if (lookback === "custom") {
    if (customFrom && customTo) return `${customFrom} → ${customTo}`;
    return "Selected range";
  }
  return "Last 30 days";
}

function getCompletedPeriodLabel(lookback) {
  if (lookback === "today") return "Completed today";
  if (lookback === "custom") return "Completed in selected range";
  return "Completed in last 30 days";
}

function CompletionRateRing({ percent = 0 }) {
  const value = Math.max(0, Math.min(100, Number(percent) || 0));
  const size = 52;
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <span className="ir-rate-ring" aria-hidden="true">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#dcfce7"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#22c55e"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="ir-rate-ring__label">{Math.round(value)}%</span>
    </span>
  );
}

function getOverviewCompletionStats(data) {
  const pending = Number(data?.pending || 0);
  const completed = Number(data?.verified || 0);
  const received = pending + completed;
  const completionRate = received > 0 ? Math.round((completed / received) * 100) : null;
  return {
    completed,
    received,
    pending,
    completionRate,
    backlogChange: pending,
  };
}

function getAnalyticsLoadingLabel(section) {
  switch (section) {
    case "metadata":
      return "Loading overview...";
    case "departments":
      return "Loading departments...";
    case "overview":
      return "Loading analytics...";
    case "leaderboard":
      return "Loading leaderboard...";
    case "eta":
      return "Loading compliance data...";
    default:
      return "Loading analytics...";
  }
}

function AnalyticsSectionShell({
  showLoading = false,
  label = "Loading...",
  className = "",
  style,
  children,
}) {
  return (
    <div
      className={className}
      style={{
        position: "relative",
        minHeight: showLoading ? "min(60vh, 720px)" : undefined,
        ...style,
      }}
    >
      {showLoading ? <LoadingOverlay show label={label} /> : null}
      <div
        style={{
          opacity: showLoading ? 0.45 : 1,
          pointerEvents: showLoading ? "none" : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function TrackingAnalytics({ onBack }) {
  const navigate = useNavigate();
  const handleBack = onBack || (() => navigate("/document-hub-v2"));
  const { instance, accounts } = useMsal();
  const { setReviewMode } = useReviewMode();

  // Hide navigation sidebar for full-screen analytics dashboard
  useEffect(() => {
    setReviewMode(true);
    return () => {
      setReviewMode(false);
    };
  }, [setReviewMode]);

  const [timeframe, setTimeframe] = useState("weekly"); // DevOps: "daily" | "weekly" | "monthly" | "yearly"
  const [overviewLookback, setOverviewLookback] = useState("30d"); // Overview: "30d" | "custom"
  const [overviewRangeFrom, setOverviewRangeFrom] = useState(() => defaultCustomRange().from);
  const [overviewRangeTo, setOverviewRangeTo] = useState(() => defaultCustomRange().to);
  const [overviewDraftFrom, setOverviewDraftFrom] = useState(() => defaultCustomRange().from);
  const [overviewDraftTo, setOverviewDraftTo] = useState(() => defaultCustomRange().to);
  const [showOverviewRangePopover, setShowOverviewRangePopover] = useState(false);
  const overviewRangePopoverRef = useRef(null);
  const [activeSection, setActiveSection] = useState("metadata"); // "metadata" | "departments" | "overview" | ...
  const [slideDirection, setSlideDirection] = useState("right"); // "right" | "left"
  const [departmentEmbedGroup, setDepartmentEmbedGroup] = useState(null);
  const [departmentEmbedKey, setDepartmentEmbedKey] = useState(0);
  const [metadataQueueView, setMetadataQueueView] = useState(null);

  useEffect(() => {
    if (!showOverviewRangePopover) return undefined;

    const handlePointerDown = (event) => {
      if (
        overviewRangePopoverRef.current &&
        !overviewRangePopoverRef.current.contains(event.target)
      ) {
        if (document.activeElement?.getAttribute?.("type") === "date") {
          return;
        }
        setShowOverviewRangePopover(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [showOverviewRangePopover]);

  useEffect(() => {
    if (activeSection !== "metadata") {
      setShowOverviewRangePopover(false);
    }
  }, [activeSection]);

  // Carousel Navigation Sections Sequence (Overview placed first)
  const CAROUSEL_SECTIONS = [
    { id: "metadata", label: "Overview" },
    { id: "departments", label: "Department" },
    { id: "overview", label: "Velocity & Status Distribution" },
    // { id: "types", label: "Work Item Types" },
    { id: "leaderboard", label: "Team Leaderboard" },
    { id: "eta", label: "ETA & SLA Compliance" },
    { id: "user-activity", label: "User Activity" },
  ];

  const getNextSectionId = (current) => {
    const idx = CAROUSEL_SECTIONS.findIndex((s) => s.id === current);
    if (idx === -1 || idx === CAROUSEL_SECTIONS.length - 1) return CAROUSEL_SECTIONS[0].id;
    return CAROUSEL_SECTIONS[idx + 1].id;
  };

  const getPrevSectionId = (current) => {
    const idx = CAROUSEL_SECTIONS.findIndex((s) => s.id === current);
    if (idx <= 0) return CAROUSEL_SECTIONS[CAROUSEL_SECTIONS.length - 1].id;
    return CAROUSEL_SECTIONS[idx - 1].id;
  };

  const handleSelectSection = (newSectionId) => {
    const oldIdx = CAROUSEL_SECTIONS.findIndex((s) => s.id === activeSection);
    const newIdx = CAROUSEL_SECTIONS.findIndex((s) => s.id === newSectionId);
    if (newIdx !== -1 && oldIdx !== -1) {
      setSlideDirection(newIdx >= oldIdx ? "right" : "left");
    }
    if (newSectionId === "departments") {
      setDepartmentEmbedGroup(null);
      setDepartmentEmbedKey((key) => key + 1);
    }
    setMetadataQueueView(null);
    setActiveSection(newSectionId);
  };

  const openDepartmentsView = (group = null) => {
    const oldIdx = CAROUSEL_SECTIONS.findIndex((s) => s.id === activeSection);
    const newIdx = CAROUSEL_SECTIONS.findIndex((s) => s.id === "departments");
    if (newIdx !== -1 && oldIdx !== -1) {
      setSlideDirection(newIdx >= oldIdx ? "right" : "left");
    }
    setDepartmentEmbedGroup(group);
    setDepartmentEmbedKey((key) => key + 1);
    setActiveSection("departments");
  };

  const openMetadataQueue = ({
    title,
    tab = "pending",
    assignedState = "all",
    olderThan24h = false,
    verifiedPeriod = "",
  }) => {
    const range = getOverviewLookbackRange(
      overviewLookback,
      overviewRangeFrom,
      overviewRangeTo,
    );
    const applyDateRange = tab === "verified" || overviewLookback === "custom";
    setMetadataQueueView({
      title,
      queueTab: tab,
      assigned_state: assignedState,
      older_than_24h: olderThan24h,
      verified_period: verifiedPeriod,
      from_date: applyDateRange ? range.from_date : undefined,
      to_date: applyDateRange ? range.to_date : undefined,
    });
  };

  const handleNextSection = () => {
    setSlideDirection("right");
    setMetadataQueueView(null);
    setActiveSection((prev) => getNextSectionId(prev));
  };

  const handlePrevSection = () => {
    setSlideDirection("left");
    setMetadataQueueView(null);
    setActiveSection((prev) => getPrevSectionId(prev));
  };

  const getSectionTitle = (id) => {
    const found = CAROUSEL_SECTIONS.find((s) => s.id === id);
    return found ? found.label : "Overview";
  };

  // Keyboard Arrow Carousel Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeSection === "all") return;
      // Don't trigger if user is typing in an input
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") {
        handleNextSection();
      } else if (e.key === "ArrowLeft") {
        handlePrevSection();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeSection]);
  const [workItemType, setWorkItemType] = useState("all"); // "all" | "User Story" | "Bug" | "Task"
  const [orgProjects, setOrgProjects] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("DWS10062665");
  const [selectedProject, setSelectedProject] = useState("ADLS Migration");
  const [selectedIteration, setSelectedIteration] = useState("all");
  const [selectedUserFilter, setSelectedUserFilter] = useState("all");
  const [showAllLeaderboard, setShowAllLeaderboard] = useState(false);
  const [showAllEta, setShowAllEta] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [loadingIterations, setLoadingIterations] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Data States
  const [summary, setSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  const [leaderboardGotoInput, setLeaderboardGotoInput] = useState("");
  const leaderboardPageSize = 5;
  const [etaData, setEtaData] = useState(null);
  const [etaPage, setEtaPage] = useState(1);
  const [etaGotoInput, setEtaGotoInput] = useState("");
  const etaPageSize = 5;
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState("");
  const [metadataReviewData, setMetadataReviewData] = useState(null);
  const loading =
    activeSection === "metadata"
      ? metadataLoading
      : isDevOpsAnalyticsSection(activeSection)
        ? analyticsLoading
        : false;

  // In-Depth User Tracker Drawer State
  const [selectedUser, setSelectedUser] = useState(null);
  const [userWorkItems, setUserWorkItems] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userTab, setUserTab] = useState("all"); // "all" | "active" | "overdue" | "completed"
  const [userSearchText, setUserSearchText] = useState("");

  // Fetch detailed work items for selected user
  useEffect(() => {
    if (!selectedUser) {
      setUserWorkItems([]);
      return;
    }
    const loadUserItems = async () => {
      try {
        setUserLoading(true);
        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/assignments/analytics/user-detail?organization=${encodeURIComponent(selectedOrg || "all")}&project=${encodeURIComponent(selectedProject || "ADLS Migration")}&iteration=${encodeURIComponent(selectedIteration || "all")}&userEmail=${encodeURIComponent(selectedUser.email || "")}&userName=${encodeURIComponent(selectedUser.name || "")}&_t=${Date.now()}`
        );
        const data = res?.data?.data || res?.data || [];
        setUserWorkItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load user detail work items:", err);
        setUserWorkItems([]);
      } finally {
        setUserLoading(false);
      }
    };
    loadUserItems();
  }, [selectedUser, selectedOrg, selectedProject, selectedIteration, instance, accounts]);

  // Load Iterations / Sprints when Org or Project changes
  useEffect(() => {
    if (!selectedOrg || selectedOrg === "all" || !selectedProject || selectedProject === "all") {
      setIterations([]);
      return;
    }
    const loadSprints = async () => {
      try {
        setLoadingIterations(true);
        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/assignments/iterations?organization=${encodeURIComponent(selectedOrg)}&project=${encodeURIComponent(selectedProject)}`
        );
        const data = res?.data?.data || res?.data || [];
        setIterations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load iterations:", err);
        setIterations([]);
      } finally {
        setLoadingIterations(false);
      }
    };
    loadSprints();
  }, [selectedOrg, selectedProject, instance, accounts]);

  // Load Available Users for current Org and Project scope (respecting reporting hierarchy)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const orgParam = encodeURIComponent(selectedOrg || "all");
        const projParam = encodeURIComponent(selectedProject || "ADLS Migration");
        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/assignments/assignable-users?organization=${orgParam}&project=${projParam}&_t=${Date.now()}`
        );
        const userList = res?.data?.data || res?.data || [];
        const validUsers = Array.isArray(userList) ? userList : [];
        const userOptions = validUsers.map((u) => {
          const val = u.displayName || u.uniqueName || u.mailAddress || u.principalName;
          return { label: u.displayName || val, value: val };
        });
        setAvailableUsers(userOptions);

        if (userOptions.length === 1) {
          setSelectedUserFilter(userOptions[0].value);
        } else if (userOptions.length > 1 && selectedUserFilter !== "all") {
          if (!userOptions.some((o) => o.value === selectedUserFilter)) {
            setSelectedUserFilter("all");
          }
        }
      } catch (err) {
        console.error("Failed to load available users for analytics:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [selectedOrg, selectedProject, instance, accounts]);

  // Load Orgs and Projects
  useEffect(() => {
    const loadOrgs = async () => {
      try {
        setLoadingOrgs(true);
        const res = await callApi(instance, accounts, "GET", "/api/assignments/organizations/projects");
        const data = res?.data?.data || res?.data || [];
        const orgList = Array.isArray(data) ? data : [];
        setOrgProjects(orgList);
        if (orgList.length > 0) {
          const firstOrg = orgList[0];
          setSelectedOrg(firstOrg.organization);
          const adlsProj = firstOrg.projects?.find(p => (p.name || p).toLowerCase() === "adls migration");
          setSelectedProject(adlsProj ? (adlsProj.name || adlsProj) : "ADLS Migration");
        }
      } catch (err) {
        console.error("Failed to load orgs for analytics:", err);
      } finally {
        setLoadingOrgs(false);
      }
    };
    loadOrgs();
  }, [instance, accounts]);

  // Load the email overview independently so it never waits for ADO analytics.
  // Overview lookback (30d / custom range) limits received_datetime for this screen only.
  const loadMetadataOverview = useCallback(async () => {
    try {
      setMetadataLoading(true);
      setError("");

      const params = new URLSearchParams({
        lookback: overviewLookback || "30d",
      });
      if (overviewLookback === "custom") {
        const range = getOverviewLookbackRange(
          "custom",
          overviewRangeFrom,
          overviewRangeTo,
        );
        if (!range.from_date || !range.to_date) {
          setMetadataLoading(false);
          return;
        }
        params.set("from_date", range.from_date);
        params.set("to_date", range.to_date);
      }

      const metaRes = await callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/analytics/metadata-review?${params.toString()}`
      );

      setMetadataReviewData(metaRes?.data?.data || null);
    } catch (err) {
      console.error("Failed to fetch email overview:", err);
      setError("Failed to load the email overview. Please try refreshing.");
    } finally {
      setMetadataLoading(false);
    }
  }, [instance, accounts, overviewLookback, overviewRangeFrom, overviewRangeTo]);

  // Load ADO and performance data for analytics sections
  const loadActiveAnalyticsSection = useCallback(async () => {
    const isVisible = isDevOpsAnalyticsSection(activeSection);
    try {
      if (isVisible) {
        setAnalyticsLoading(true);
      }
      setError("");

      const orgParam = encodeURIComponent(selectedOrg || "DWS10062665");
      const projParam = encodeURIComponent(selectedProject || "ADLS Migration");
      const iterParam = encodeURIComponent(selectedIteration || "all");
      const userParam = encodeURIComponent(selectedUserFilter || "all");

      const summaryRequest = callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/analytics/summary?organization=${orgParam}&project=${projParam}&timeframe=${timeframe}&workItemType=${encodeURIComponent(workItemType)}&iteration=${iterParam}&assignedTo=${userParam}`
      );
      const performanceRequest = callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/analytics/user-performance?organization=${orgParam}&project=${projParam}&timeframe=${timeframe}&iteration=${iterParam}&assignedTo=${userParam}`
      );
      const etaRequest = callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/analytics/eta-tracker?organization=${orgParam}&project=${projParam}&iteration=${iterParam}&assignedTo=${userParam}`
      );

      const [sumRes, perfRes, etaRes] = await Promise.all([
        summaryRequest,
        performanceRequest,
        etaRequest,
      ]);

      setSummary(sumRes?.data?.data || null);
      if (perfRes) {
        setLeaderboard(perfRes?.data?.data || []);
      }
      if (etaRes) {
        setEtaData(etaRes?.data?.data || null);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
      if (isVisible) {
        setError("Failed to load performance analytics. Please try refreshing.");
      }
    } finally {
      setAnalyticsLoading(false);
    }
  }, [
    activeSection,
    selectedOrg,
    selectedProject,
    selectedIteration,
    selectedUserFilter,
    timeframe,
    workItemType,
    instance,
    accounts,
  ]);

  useEffect(() => {
    if (!accounts.length) return;
    loadMetadataOverview();
  }, [accounts.length, loadMetadataOverview]);

  useEffect(() => {
    if (!accounts.length) return;
    loadActiveAnalyticsSection();
  }, [accounts.length, activeSection, loadActiveAnalyticsSection]);

  const refreshActiveSection = useCallback(() => {
    if (activeSection === "metadata") {
      return loadMetadataOverview();
    }
    if (activeSection === "departments") {
      setDepartmentEmbedKey((key) => key + 1);
      return;
    }
    if (isDevOpsAnalyticsSection(activeSection)) {
      return loadActiveAnalyticsSection();
    }
  }, [activeSection, loadMetadataOverview, loadActiveAnalyticsSection]);

  return (
    <div style={{ ...s.page, position: "relative" }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes trackingLoadBar {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
      `}</style>

      {/* ULTRA-COMPACT EXECUTIVE HEADER BAR */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: "16px",
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "14px",
          padding: "14px 20px",
          minHeight: "64px",
          marginBottom: "12px",
          boxShadow: "0 1px 4px rgba(15,23,42,0.04)",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifySelf: "start" }}>
          <button
            type="button"
            onClick={handleBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              color: "#475569",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.15s ease",
              outline: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f1f5f9";
              e.currentTarget.style.color = THEME;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f8fafc";
              e.currentTarget.style.color = "#475569";
            }}
          >
            <ArrowLeft size={13} /> Back
          </button>
        </div>

        <div
          style={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            justifySelf: "center",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "17.5px", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.3px" }}>
            System Analytics
          </h1>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", justifySelf: "end" }}>
          <button
            type="button"
            onClick={refreshActiveSection}
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 14px",
              borderRadius: "8px",
              border: "none",
              background: loading ? "#94a3b8" : THEME,
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "700",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.18s ease",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
              outline: "none",
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.transform = "none";
            }}
          >
            <RefreshCw
              size={12}
              style={{
                animation: loading ? "trackingSpinAnim 0.8s linear infinite" : "none",
              }}
            />
            <span>{loading ? "Syncing..." : "Refresh Data"}</span>
          </button>
        </div>
      </div>

      {/* CENTER TOP INTERACTIVE NAVIGATION & TIMEFRAME TOOLBAR (STRICT SINGLE LINE WITH HORIZONTAL SCROLL) */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          margin: "0 0 12px 0"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            gap: "10px",
            background: "#ffffff",
            padding: "8px 14px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 20px -3px rgba(15,23,42,0.07)",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box"
          }}
        >
          {/* Section Navigation Pills (Horizontally Scrollable when exceeding width) */}
          <div
            className="analytics-pills-scroll"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexWrap: "nowrap",
              overflowX: "auto",
              flex: 1,
              minWidth: 0,
              padding: "2px 0",
              scrollbarWidth: "none"
            }}
          >
            {[
              { id: "metadata", label: "Overview", icon: LayoutDashboard, badge: formatInboxCount(metadataReviewData?.pending) },
              { id: "departments", label: "Department", icon: Layers },
              { id: "overview", label: "Velocity & Status Distribution", icon: PieChart },
              // { id: "types", label: "Work Item Types", icon: Tag },
              { id: "leaderboard", label: "Team Leaderboard", icon: Users, badge: leaderboard.length || (metadataReviewData?.workload?.length || metadataReviewData?.people?.length || 0) },
              { id: "eta", label: "ETA & SLA Compliance", icon: Target, badge: etaData?.overdueCount || summary?.overdueCount || metadataReviewData?.older_than_24h || 0 },
              { id: "user-activity", label: "User Activity", icon: BarChart3 },
            ].map((tab) => {
              const Icon = tab.icon;
              const isSel = activeSection === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleSelectSection(tab.id)}
                  className="analytics-nav-pill"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "7px",
                    padding: "8px 14px",
                    borderRadius: "10px",
                    border: isSel ? `1px solid ${THEME}` : "1px solid transparent",
                    background: isSel ? THEME : "transparent",
                    color: isSel ? "#ffffff" : "#475569",
                    fontWeight: isSel ? "700" : "600",
                    fontSize: "13px",
                    cursor: "pointer",
                    transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: isSel ? "0 4px 14px rgba(37, 99, 235, 0.28)" : "none",
                    outline: "none",
                    position: "relative",
                    flexShrink: 0,
                    whiteSpace: "nowrap"
                  }}
                >
                  <Icon size={14} color={isSel ? "#ffffff" : "#64748b"} />
                  <span>{tab.label}</span>
                  {tab.badge !== undefined && (
                    <span
                      style={{
                        padding: "2px 7px",
                        borderRadius: "10px",
                        fontSize: "10.5px",
                        fontWeight: "800",
                        background: isSel ? "rgba(255,255,255,0.28)" : tab.id === "eta" ? "#fee2e2" : tab.id === "metadata" ? "#fef3c7" : "#eff6ff",
                        color: isSel ? "#ffffff" : tab.id === "eta" ? "#dc2626" : tab.id === "metadata" ? "#b45309" : "#2563eb",
                        transition: "all 0.15s ease"
                      }}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Vertical Divider Line */}
          <div style={{ width: "1px", height: "26px", background: "#cbd5e1", margin: "0 4px", flexShrink: 0 }} />

          {activeSection === "metadata" ? (
            <div
              ref={overviewRangePopoverRef}
              title="Control how much Overview data is loaded"
              style={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                flexShrink: 0,
                whiteSpace: "nowrap"
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "#f1f5f9",
                  padding: "3px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {OVERVIEW_LOOKBACKS.map((option) => {
                  const isCustom = option.id === "custom";
                  const isActive = isCustom
                    ? showOverviewRangePopover || overviewLookback === "custom"
                    : !showOverviewRangePopover && overviewLookback === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        if (isCustom) {
                          const seeded = defaultCustomRange();
                          setOverviewDraftFrom(
                            overviewLookback === "custom" && overviewRangeFrom
                              ? overviewRangeFrom
                              : seeded.from,
                          );
                          setOverviewDraftTo(
                            overviewLookback === "custom" && overviewRangeTo
                              ? overviewRangeTo
                              : seeded.to,
                          );
                          setShowOverviewRangePopover((open) =>
                            overviewLookback === "custom" ? !open : true,
                          );
                          return;
                        }
                        setShowOverviewRangePopover(false);
                        setOverviewLookback(option.id);
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 12px",
                        borderRadius: "7px",
                        border: "none",
                        background: isActive ? THEME : "transparent",
                        color: isActive ? "#ffffff" : "#64748b",
                        fontWeight: isActive ? "700" : "600",
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: "all 0.18s ease",
                        boxShadow: isActive ? "0 2px 8px rgba(37, 99, 235, 0.25)" : "none",
                        outline: "none",
                        flexShrink: 0,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {showOverviewRangePopover ? (
                <div
                  role="dialog"
                  aria-label="Select date range"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    zIndex: 40,
                    width: "280px",
                    padding: "12px",
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    boxShadow: "0 12px 32px -10px rgba(15, 23, 42, 0.22)",
                    whiteSpace: "normal",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: "10px",
                    }}
                  >
                    Select date range
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b" }}>From</span>
                      <input
                        type="date"
                        value={overviewDraftFrom}
                        max={overviewDraftTo || undefined}
                        onChange={(e) => setOverviewDraftFrom(e.target.value)}
                        style={{
                          height: "34px",
                          padding: "0 8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          fontSize: "12px",
                          color: "#334155",
                          background: "#fff",
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b" }}>To</span>
                      <input
                        type="date"
                        value={overviewDraftTo}
                        min={overviewDraftFrom || undefined}
                        onChange={(e) => setOverviewDraftTo(e.target.value)}
                        style={{
                          height: "34px",
                          padding: "0 8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          fontSize: "12px",
                          color: "#334155",
                          background: "#fff",
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                    </label>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "8px",
                      marginTop: "12px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const seeded = defaultCustomRange();
                        setOverviewDraftFrom(seeded.from);
                        setOverviewDraftTo(seeded.to);
                        setOverviewRangeFrom(seeded.from);
                        setOverviewRangeTo(seeded.to);
                        setOverviewLookback("30d");
                        setShowOverviewRangePopover(false);
                      }}
                      style={{
                        height: "32px",
                        padding: "0 12px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        background: "#fff",
                        color: "#475569",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      disabled={!overviewDraftFrom || !overviewDraftTo}
                      onClick={() => {
                        if (!overviewDraftFrom || !overviewDraftTo) return;
                        setOverviewRangeFrom(overviewDraftFrom);
                        setOverviewRangeTo(overviewDraftTo);
                        setOverviewLookback("custom");
                        setShowOverviewRangePopover(false);
                      }}
                      style={{
                        height: "32px",
                        padding: "0 12px",
                        borderRadius: "8px",
                        border: "none",
                        background: !overviewDraftFrom || !overviewDraftTo ? "#93c5fd" : THEME,
                        color: "#ffffff",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: !overviewDraftFrom || !overviewDraftTo ? "not-allowed" : "pointer",
                      }}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              title={
                !isDevOpsAnalyticsSection(activeSection)
                  ? "Timeframe filtering applies to DevOps Work Items"
                  : "Filter analytics by created period"
              }
              style={{
                display: "flex",
                alignItems: "center",
                background: !isDevOpsAnalyticsSection(activeSection) ? "#f8fafc" : "#f1f5f9",
                padding: "3px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                opacity: !isDevOpsAnalyticsSection(activeSection) ? 0.45 : 1,
                pointerEvents: !isDevOpsAnalyticsSection(activeSection) ? "none" : "auto",
                cursor: !isDevOpsAnalyticsSection(activeSection) ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                flexShrink: 0,
                whiteSpace: "nowrap"
              }}
            >
              {["daily", "weekly", "monthly", "yearly"].map((tf) => {
                const isTfActive = timeframe === tf;
                const timeframeEnabled = isDevOpsAnalyticsSection(activeSection);
                return (
                  <button
                    key={tf}
                    onClick={() => setTimeframe(tf)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "7px",
                      border: "none",
                      background: isTfActive && timeframeEnabled ? THEME : "transparent",
                      color: isTfActive && timeframeEnabled ? "#ffffff" : "#64748b",
                      fontWeight: isTfActive ? "700" : "600",
                      fontSize: "12px",
                      cursor: !timeframeEnabled ? "not-allowed" : "pointer",
                      transition: "all 0.18s ease",
                      boxShadow: isTfActive && timeframeEnabled ? "0 2px 8px rgba(37, 99, 235, 0.25)" : "none",
                      outline: "none",
                      flexShrink: 0,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {tf.charAt(0).toUpperCase() + tf.slice(1)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-6px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes livePingAnim {
          75%, 100% {
            transform: scale(2.4);
            opacity: 0;
          }
        }
        @keyframes analyticsSlideFromRight {
          0% {
            opacity: 0;
            transform: translateX(40px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes analyticsSlideFromLeft {
          0% {
            opacity: 0;
            transform: translateX(-40px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .analytics-slide-right {
          animation: analyticsSlideFromRight 0.32s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .analytics-slide-left {
          animation: analyticsSlideFromLeft 0.32s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .analytics-nav-pill:not([style*="rgb(37, 99, 235)"]):hover {
          background-color: #f1f5f9 !important;
          color: #1e293b !important;
          transform: translateY(-1px);
        }
        .analytics-pills-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .analytics-pills-scroll::-webkit-scrollbar {
          display: none;
          height: 0px;
        }
      `}</style>

      {error && (
        <div style={s.errorBanner}>
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* SECTION 0: DOCUMENT INDEX HUB OVERVIEW SECTION */}
      {activeSection === "metadata" && (
        <AnalyticsSectionShell
          showLoading={loading}
          label={getAnalyticsLoadingLabel("metadata")}
          className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"}
        >
        {metadataQueueView ? (
          <div key={`metadata-queue-${metadataQueueView.title}-${overviewLookback}-${overviewRangeFrom}-${overviewRangeTo}`}>
            <MetadataReview
              embedMode="queue"
              initialQueueFilter={{
                ...metadataQueueView,
                ...getOverviewLookbackRange(
                  overviewLookback,
                  overviewRangeFrom,
                  overviewRangeTo,
                ),
              }}
              embedQueueTitle={metadataQueueView.title}
              embedQueueCountLabel={
                metadataQueueView.title === "Pending Tasks"
                  ? "Pending Records"
                  : metadataQueueView.title
              }
              embedQueueBackLabel="Back to overview"
              onEmbedQueueBack={() => setMetadataQueueView(null)}
            />
          </div>
        ) : (
        <div
          key={`section-metadata-${activeSection}-${slideDirection}`}
          style={{ display: "flex", flexDirection: "column", gap: "8px" }}
        >
          {/* Section 1: Pending Tasks */}
          <div className="ir-overview-section" aria-busy={loading && !metadataReviewData}>
            <div className="ir-overview-section__title">Pending Tasks</div>
            <div className="ir-kpi-grid">
              {/* 1. Pending Tasks */}
              <button
                type="button"
                className="ir-kpi ir-kpi--pending is-clickable"
                disabled={loading && !metadataReviewData}
                onClick={() => openMetadataQueue({ title: "Pending Tasks" })}
              >
                <div className="ir-kpi__value">
                  {loading && !metadataReviewData ? (
                    <MetricSkel variant="value" />
                  ) : (
                    formatInboxCount(metadataReviewData?.pending)
                  )}
                </div>
                <div className="ir-kpi__label">Pending Tasks</div>
                <div className="ir-kpi__hint">Active backlog</div>
              </button>

              {/* 2. Unassigned */}
              <button
                type="button"
                className="ir-kpi ir-kpi--unassigned is-clickable"
                disabled={loading && !metadataReviewData}
                onClick={() =>
                  openMetadataQueue({
                    title: "Unassigned Tasks",
                    assignedState: "unassigned",
                  })
                }
              >
                <div className="ir-kpi__value">
                  {loading && !metadataReviewData ? (
                    <MetricSkel variant="value" />
                  ) : (
                    formatInboxCount(metadataReviewData?.unassigned)
                  )}
                </div>
                <div className="ir-kpi__label">Unassigned Tasks</div>
                <div className="ir-kpi__hint">
                  {metadataReviewData?.pending
                    ? `${Math.round(((metadataReviewData.unassigned || 0) / metadataReviewData.pending) * 100)}% of total`
                    : "0% of total"}
                </div>
              </button>

              {/* 3. Assigned */}
              <button
                type="button"
                className="ir-kpi ir-kpi--assigned is-clickable"
                disabled={loading && !metadataReviewData}
                onClick={() =>
                  openMetadataQueue({
                    title: "Assigned Tasks",
                    assignedState: "assigned",
                  })
                }
              >
                <div className="ir-kpi__value">
                  {loading && !metadataReviewData ? (
                    <MetricSkel variant="value" />
                  ) : (
                    formatInboxCount(metadataReviewData?.assigned)
                  )}
                </div>
                <div className="ir-kpi__label">Assigned Tasks</div>
                <div className="ir-kpi__hint">
                  {metadataReviewData?.pending
                    ? `${Math.round(((metadataReviewData.assigned || 0) / metadataReviewData.pending) * 100)}% of total`
                    : "0% of total"}
                </div>
              </button>
            </div>
          </div>

          {/* Section 2: Completed Tasks */}
          <div className="ir-overview-section" aria-busy={loading && !metadataReviewData}>
            <div className="ir-overview-section__title">Completed Tasks</div>
            {(() => {
              const stats = getOverviewCompletionStats(metadataReviewData);
              const lookbackHint = getLookbackHint(
                overviewLookback,
                overviewRangeFrom,
                overviewRangeTo,
              );
              return (
                <div className="ir-completed-strip">
                  <button
                    type="button"
                    className="ir-completed-metric"
                    disabled={loading && !metadataReviewData}
                    onClick={() =>
                      openMetadataQueue({
                        title: getCompletedPeriodLabel(overviewLookback),
                        tab: "verified",
                      })
                    }
                  >
                    <span className="ir-completed-metric__icon">
                      <CheckCircle2 size={22} />
                    </span>
                    <span className="ir-completed-metric__copy">
                      <span className="ir-completed-metric__value">
                        {loading && !metadataReviewData ? (
                          <MetricSkel variant="value" />
                        ) : (
                          formatInboxCount(stats.completed)
                        )}
                      </span>
                      <span className="ir-completed-metric__label">Completed</span>
                      <span className="ir-completed-metric__hint">{lookbackHint}</span>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="ir-completed-metric"
                    disabled={loading && !metadataReviewData}
                    onClick={() =>
                      openMetadataQueue({
                        title: "Completion rate",
                        tab: "verified",
                      })
                    }
                  >
                    {loading && !metadataReviewData ? (
                      <MetricSkel variant="value" />
                    ) : (
                      <CompletionRateRing percent={stats.completionRate || 0} />
                    )}
                    <span className="ir-completed-metric__copy">
                      <span className="ir-completed-metric__value">
                        {loading && !metadataReviewData ? (
                          <MetricSkel variant="value" />
                        ) : stats.completionRate == null ? (
                          "—"
                        ) : (
                          `${stats.completionRate}%`
                        )}
                      </span>
                      <span className="ir-completed-metric__label">Completion rate</span>
                      <span className="ir-completed-metric__hint">
                        {stats.received
                          ? `${formatInboxCount(stats.completed)} of ${formatInboxCount(stats.received)} received`
                          : lookbackHint}
                      </span>
                    </span>
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Section 3: Pending Band (Departments + Action Center) */}
          <div className="ir-pending-band">
            {/* Left: Departments Panel */}
            <div className="ir-panel">
              <div className="ir-panel__title ir-panel__title--split">
                <span>Departments</span>
                <button
                  type="button"
                  className="ir-panel__link"
                  onClick={() => openDepartmentsView(null)}
                >
                  View all
                </button>
              </div>
              <div className="ir-dept-mini-grid" aria-busy={loading && !metadataReviewData}>
                {loading && !metadataReviewData ? (
                  [0, 1, 2].map((i) => (
                    <div key={`dept-skel-${i}`} className="ir-dept-tile ir-dept-tile--skel">
                      <MetricSkel variant="pill" />
                      <div className="ir-dept-tile__value">
                        <MetricSkel variant="value" />
                      </div>
                      <MetricSkel variant="hint" />
                    </div>
                  ))
                ) : (
                  (
                    namedDepartments(metadataReviewData?.departments || []).length > 0
                      ? namedDepartments(metadataReviewData.departments)
                      : [
                          { department_id: "claims", department_name: "Claims", pending_count: 0, older_than_24h: 0 },
                          { department_id: "cs", department_name: "Customer Service", pending_count: 0, older_than_24h: 0 },
                          { department_id: "fin", department_name: "Finance", pending_count: 0, older_than_24h: 0 },
                          { department_id: "ops", department_name: "Operation", pending_count: 3, older_than_24h: 3 },
                          { department_id: "uw", department_name: "Underwriting", pending_count: 1, older_than_24h: 1 }
                        ]
                  ).map((dept) => {
                    const tone = departmentTone(dept);
                    return (
                      <button
                        key={String(dept.department_id || dept.department_name)}
                        type="button"
                        className="ir-dept-tile"
                        onClick={() => openDepartmentsView(dept)}
                      >
                        <div className={`ir-dept-pill ir-dept-pill--${tone}`}>
                          {dept.department_name}
                        </div>
                        <div className="ir-dept-tile__value">
                          {formatInboxCount(dept.pending_count || 0)}
                        </div>
                        <div className="ir-dept-tile__hint">Pending</div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right: Action Center Panel */}
            <div className="ir-panel ir-action-center">
              <div className="ir-action-center__title">
                <AlertTriangle size={16} />
                Action Center
              </div>
              <div className="ir-action-center__list" aria-busy={loading && !metadataReviewData}>
                {[
                  {
                    key: "delay",
                    label: "At risk of delay",
                    hint: "Unassigned",
                    value: formatInboxCount(metadataReviewData?.unassigned),
                    filter: { title: "At risk of delay", assignedState: "unassigned" }
                  },
                  {
                    key: "mine",
                    label: "Assigned to you",
                    hint: "Your queue",
                    value: formatInboxCount(metadataReviewData?.my_pending ?? 0),
                    filter: { title: "Assigned to you", assignedState: "mine" }
                  },
                  {
                    key: "teams",
                    label: "Assigned to teams",
                    hint: "In progress",
                    value: formatInboxCount(metadataReviewData?.assigned),
                    filter: { title: "Assigned to teams", assignedState: "assigned" }
                  }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`ir-action-row ir-action-row--${item.key}${loading && !metadataReviewData ? " is-loading" : ""}`}
                    disabled={loading && !metadataReviewData}
                    onClick={() => openMetadataQueue(item.filter)}
                  >
                    <span className={`ir-action-dot ir-action-dot--${item.key}`} />
                    <div className="ir-action-row__copy">
                      <div className="ir-action-row__label">{item.label}</div>
                      <div className="ir-action-row__hint">{item.hint}</div>
                    </div>
                    <div className="ir-action-row__value">
                      {loading && !metadataReviewData ? <MetricSkel variant="row" /> : item.value}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}
        </AnalyticsSectionShell>
      )}

      {activeSection === "departments" && (
        <AnalyticsSectionShell
          showLoading={loading}
          label={getAnalyticsLoadingLabel("departments")}
          className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"}
        >
        <div key={`section-departments-${departmentEmbedKey}`}>
          <MetadataReview
            embedMode="groups"
            initialSelectedGroup={departmentEmbedGroup}
            embedGroupsBackLabel="Back to departments"
          />
        </div>
        </AnalyticsSectionShell>
      )}

      {/* DEVOPS ANALYTICS PANEL — filters, KPI cards, and active tab content share one loading shell */}
      {isDevOpsAnalyticsSection(activeSection) && (
        <AnalyticsSectionShell
          showLoading={loading}
          label={getAnalyticsLoadingLabel(activeSection)}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
            <div style={{ ...s.filterCard, marginBottom: 0, padding: "10px 16px", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", width: "100%" }}>
                <Filter size={15} color="#64748b" />
                <span style={s.filterLabel}>FILTERS:</span>

                <CustomDropdown
                  label="Sprint"
                  icon={GitBranch}
                  value={selectedIteration}
                  loading={loadingIterations}
                  disabled={loadingIterations || selectedOrg === "all" || selectedProject === "all"}
                  disabledHint={selectedOrg === "all" || selectedProject === "all" ? "Select Project first" : ""}
                  onChange={(val) => setSelectedIteration(val)}
                  options={[
                    { label: "All Sprints", value: "all" },
                    ...iterations.map((iter) => {
                      const iterName = iter.name || iter.path || String(iter);
                      return { label: iterName, value: iterName };
                    })
                  ]}
                />

                <CustomDropdown
                  label="Type"
                  icon={Tag}
                  value={workItemType}
                  loading={loading}
                  onChange={(val) => setWorkItemType(val)}
                  options={[
                    { label: "All Types", value: "all" },
                    { label: "User Stories", value: "User Story" },
                    { label: "Bugs", value: "Bug" },
                    { label: "Tasks", value: "Task" },
                    { label: "Requirements", value: "Requirement" }
                  ]}
                />

                <CustomDropdown
                  label="User"
                  icon={User}
                  value={selectedUserFilter}
                  loading={loadingUsers}
                  onChange={(val) => setSelectedUserFilter(val)}
                  options={[
                    ...(availableUsers.length > 1 ? [{ label: "All Users", value: "all" }] : []),
                    ...availableUsers
                  ]}
                />
              </div>
            </div>
          </div>

        <div key={`kpi-grid-${activeSection}-${slideDirection}`} className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"} style={s.kpiGrid}>
          {loading && !summary ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={`kpi-skel-${i}`} style={s.kpiCard}>
                <div style={s.kpiHeader}>
                  <div style={{ ...s.skeletonBar, width: "110px", height: "12px" }} />
                  <div style={{ ...s.skeletonBar, width: "32px", height: "32px", borderRadius: "8px" }} />
                </div>
                <div style={{ ...s.skeletonBar, width: "80px", height: "28px", margin: "14px 0 10px 0" }} />
                <div style={s.kpiFooter}>
                  <div style={{ ...s.skeletonBar, width: "130px", height: "10px" }} />
                </div>
                <div style={s.progressBarTrack}>
                  <div style={{ ...s.skeletonBar, width: "100%", height: "100%" }} />
                </div>
              </div>
            ))
          ) : (
            <>
              {/* ──────── 1. OVERVIEW & VELOCITY CARDS ──────── */}
            {activeSection === "overview" && (
              <>
                {/* Card 1: Total Work Items */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Total Work Items</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(37, 99, 235, 0.1)", color: THEME }}>
                      <BarChart3 size={18} />
                    </div>
                  </div>
                  <div style={s.kpiValue}>{summary?.totalCount || 0}</div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#16a34a", fontWeight: "700" }}>{summary?.completedCount || 0} Closed</span>
                    <span style={{ color: "#94a3b8" }}>•</span>
                    <span style={{ color: "#2563eb", fontWeight: "700" }}>{summary?.activeCount || 0} Active</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${summary?.totalCount ? Math.min(100, Math.round((summary.completedCount / summary.totalCount) * 100)) : 0}%`,
                        background: THEME
                      }}
                    />
                  </div>
                </div>

                {/* Card 2: User Story Completion Rate */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Story Completion Rate</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>
                      <CheckCircle2 size={18} />
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ ...s.kpiValue, color: "#10b981" }}>{summary?.completionRate || 0}%</div>
                    <SVGRadialGauge percent={summary?.completionRate || 0} size={48} color="#10b981" />
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#64748b" }}>Target Velocity Achieved</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${summary?.completionRate || 0}%`,
                        background: "#10b981"
                      }}
                    />
                  </div>
                </div>

                {/* Card 3: ETA Compliance SLA */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>ETA SLA Compliance</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>
                      <Target size={18} />
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ ...s.kpiValue, color: (summary?.etaComplianceRate ?? 100) >= 80 ? "#10b981" : "#ef4444" }}>
                      {summary?.etaComplianceRate ?? 100}%
                    </div>
                    <SVGRadialGauge percent={summary?.etaComplianceRate ?? 100} size={48} color={(summary?.etaComplianceRate ?? 100) >= 80 ? "#10b981" : "#ef4444"} />
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: summary?.overdueCount > 0 ? "#ef4444" : "#10b981", fontWeight: "700" }}>
                      {summary?.overdueCount || 0} Overdue Target Items
                    </span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${summary?.etaComplianceRate ?? 100}%`,
                        background: (summary?.etaComplianceRate ?? 100) >= 80 ? "#10b981" : "#ef4444"
                      }}
                    />
                  </div>
                </div>

                {/* Card 4: Avg Resolution Days */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Avg Resolution Speed</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
                      <Clock size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#8b5cf6" }}>{summary?.avgCycleDays || "0.0"} <span style={{ fontSize: "16px", color: "#64748b" }}>Days</span></div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#64748b" }}>Avg Time to Close Story</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `85%`,
                        background: "#8b5cf6"
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* ──────── 2. WORK ITEM TYPES CARDS (temporarily hidden) ──────── */}
            {false && activeSection === "types" && (
              <>
                {/* Type Card 1: User Stories */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>User Stories</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(37, 99, 235, 0.1)", color: "#2563eb" }}>
                      <Tag size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#2563eb" }}>{summary?.typeCounts?.["User Story"] || 0}</div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#2563eb", fontWeight: "700" }}>
                      {Math.round(((summary?.typeCounts?.["User Story"] || 0) / (summary?.totalCount || 1)) * 100)}% of Volume
                    </span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${Math.min(100, Math.round(((summary?.typeCounts?.["User Story"] || 0) / (summary?.totalCount || 1)) * 100))}%`,
                        background: "#2563eb"
                      }}
                    />
                  </div>
                </div>

                {/* Type Card 2: Bugs & Defects */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Bugs &amp; Defects</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(239, 68, 68, 0.1)", color: "#ef4444" }}>
                      <AlertTriangle size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#ef4444" }}>{summary?.typeCounts?.["Bug"] || 0}</div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: (summary?.typeCounts?.["Bug"] || 0) > 0 ? "#ef4444" : "#10b981", fontWeight: "700" }}>
                      {Math.round(((summary?.typeCounts?.["Bug"] || 0) / (summary?.totalCount || 1)) * 100)}% Defect Ratio
                    </span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${Math.min(100, Math.round(((summary?.typeCounts?.["Bug"] || 0) / (summary?.totalCount || 1)) * 100))}%`,
                        background: "#ef4444"
                      }}
                    />
                  </div>
                </div>

                {/* Type Card 3: Tasks & Operations */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Tasks &amp; Ops</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>
                      <Layers size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#10b981" }}>{summary?.typeCounts?.["Task"] || 0}</div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#64748b" }}>Operational Work Items</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${Math.min(100, Math.round(((summary?.typeCounts?.["Task"] || 0) / (summary?.totalCount || 1)) * 100))}%`,
                        background: "#10b981"
                      }}
                    />
                  </div>
                </div>

                {/* Type Card 4: Dominant Work Stream */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Dominant Stream</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
                      <Target size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, fontSize: "20px", color: "#8b5cf6", lineHeight: "1.3", margin: "8px 0 6px 0" }}>
                    {Object.entries(summary?.typeCounts || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "User Story"}
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#64748b" }}>Highest Volume Category</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div style={{ ...s.progressBarFill, width: "90%", background: "#8b5cf6" }} />
                  </div>
                </div>
              </>
            )}

            {/* ──────── 3. TEAM LEADERBOARD CARDS ──────── */}
            {activeSection === "leaderboard" && (
              <>
                {/* Team Card 1: Active Team Members */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Tracked Assignees</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
                      <Users size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#8b5cf6" }}>{leaderboard.length} <span style={{ fontSize: "16px", color: "#64748b" }}>Members</span></div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#10b981", fontWeight: "700" }}>Active Contributors</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div style={{ ...s.progressBarFill, width: "100%", background: "#8b5cf6" }} />
                  </div>
                </div>

                {/* Team Card 2: Top Workload Lead */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Workload Leader</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(37, 99, 235, 0.1)", color: "#2563eb" }}>
                      <Award size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, fontSize: "18px", color: "#2563eb", lineHeight: "1.3", margin: "8px 0 6px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {leaderboard[0]?.user ? leaderboard[0].user.split("@")[0].replace("idx.", "") : "None"}
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#2563eb", fontWeight: "700" }}>
                      {leaderboard[0]?.totalAssigned || 0} Items ({leaderboard[0]?.workloadPct || 0}% Load)
                    </span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div style={{ ...s.progressBarFill, width: `${leaderboard[0]?.workloadPct || 50}%`, background: "#2563eb" }} />
                  </div>
                </div>

                {/* Team Card 3: Avg Load per Dev */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Avg Load / Member</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>
                      <BarChart3 size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#10b981" }}>
                    {leaderboard.length ? ((summary?.totalCount || 0) / leaderboard.length).toFixed(1) : "0"}{" "}
                    <span style={{ fontSize: "16px", color: "#64748b" }}>Items</span>
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#64748b" }}>Balanced Team Workload</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div style={{ ...s.progressBarFill, width: "75%", background: "#10b981" }} />
                  </div>
                </div>

                {/* Team Card 4: Team Overdue Count */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Team Overdue Load</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>
                      <AlertTriangle size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: (summary?.overdueCount || 0) > 0 ? "#ef4444" : "#10b981" }}>
                    {summary?.overdueCount || 0} <span style={{ fontSize: "16px", color: "#64748b" }}>Overdue</span>
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: (summary?.overdueCount || 0) > 0 ? "#ef4444" : "#10b981", fontWeight: "700" }}>
                      Distributed Across {leaderboard.length} Members
                    </span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div style={{ ...s.progressBarFill, width: `${Math.min(100, (summary?.overdueCount || 0) * 10)}%`, background: "#f59e0b" }} />
                  </div>
                </div>
              </>
            )}

            {/* ──────── 4. ETA & SLA COMPLIANCE CARDS ──────── */}
            {activeSection === "eta" && (
              <>
                {/* ETA Card 1: ETA SLA Compliance Rate */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>ETA SLA Compliance</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>
                      <Target size={18} />
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ ...s.kpiValue, color: (summary?.etaComplianceRate ?? 100) >= 80 ? "#10b981" : "#ef4444" }}>
                      {summary?.etaComplianceRate ?? 100}%
                    </div>
                    <SVGRadialGauge percent={summary?.etaComplianceRate ?? 100} size={48} color={(summary?.etaComplianceRate ?? 100) >= 80 ? "#10b981" : "#ef4444"} />
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: (summary?.etaComplianceRate ?? 100) >= 80 ? "#10b981" : "#ef4444", fontWeight: "700" }}>
                      On-Time SLA Delivery Rate
                    </span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${summary?.etaComplianceRate ?? 100}%`,
                        background: (summary?.etaComplianceRate ?? 100) >= 80 ? "#10b981" : "#ef4444"
                      }}
                    />
                  </div>
                </div>

                {/* ETA Card 2: Critical Overdue Items */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Critical Overdue</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(220, 38, 38, 0.1)", color: "#dc2626" }}>
                      <AlertTriangle size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#dc2626" }}>
                    {etaData?.overdueCount || summary?.overdueCount || 0} <span style={{ fontSize: "16px", color: "#64748b" }}>Items</span>
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#dc2626", fontWeight: "700" }}>Immediate Action Required</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${Math.min(100, ((etaData?.overdueCount || summary?.overdueCount || 0) / (summary?.totalCount || 1)) * 100)}%`,
                        background: "#dc2626"
                      }}
                    />
                  </div>
                </div>

                {/* ETA Card 3: On-Time Deliveries */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>On-Time Deliveries</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>
                      <CheckCircle2 size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#10b981" }}>
                    {Math.max(0, (summary?.totalCount || 0) - (etaData?.overdueCount || summary?.overdueCount || 0))}{" "}
                    <span style={{ fontSize: "16px", color: "#64748b" }}>Items</span>
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#10b981", fontWeight: "700" }}>Tracking On Schedule</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${Math.min(100, ((Math.max(0, (summary?.totalCount || 0) - (etaData?.overdueCount || summary?.overdueCount || 0))) / (summary?.totalCount || 1)) * 100)}%`,
                        background: "#10b981"
                      }}
                    />
                  </div>
                </div>

                {/* ETA Card 4: Overdue Scope % */}
                <div style={s.kpiCard}>
                  <div style={s.kpiHeader}>
                    <span style={s.kpiTitle}>Overdue Scope Ratio</span>
                    <div style={{ ...s.kpiIcon, background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
                      <Clock size={18} />
                    </div>
                  </div>
                  <div style={{ ...s.kpiValue, color: "#8b5cf6" }}>
                    {Math.round(((etaData?.overdueCount || summary?.overdueCount || 0) / (summary?.totalCount || 1)) * 100)}%
                  </div>
                  <div style={s.kpiFooter}>
                    <span style={{ color: "#64748b" }}>Of Total Workload Past SLA</span>
                  </div>
                  <div style={s.progressBarTrack}>
                    <div
                      style={{
                        ...s.progressBarFill,
                        width: `${Math.round(((etaData?.overdueCount || summary?.overdueCount || 0) / (summary?.totalCount || 1)) * 100)}%`,
                        background: "#8b5cf6"
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* SECTION 1: TWO COLUMN ANALYTICS GRID (OVERVIEW & VELOCITY) */}
      {activeSection === "overview" && (
      <div key={`section-overview-${activeSection}-${slideDirection}`} className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"} style={s.chartsGrid}>
        {/* LEFT COLUMN: User Story Status Breakdown */}
        <div style={s.columnCard}>
          <div style={s.cardHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <PieChart size={18} color={THEME} />
              <h3 style={s.cardTitle}>Status Visual Distribution</h3>
            </div>
            <span style={s.badgeLabel}>{timeframe.toUpperCase()}</span>
          </div>

          {loading && !summary ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "24px", padding: "10px" }}>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div style={{ ...s.skeletonBar, width: "130px", height: "130px", borderRadius: "50%" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ ...s.skeletonBar, width: "100%", height: "14px" }} />
                <div style={{ ...s.skeletonBar, width: "100%", height: "14px" }} />
                <div style={{ ...s.skeletonBar, width: "100%", height: "14px" }} />
              </div>
            </div>
          ) : (
            <>
              {/* SVG Donut Chart for Statuses */}
              {summary?.statusCounts && Object.keys(summary.statusCounts).length > 0 && (
                <SVGDonutChart
                  data={Object.entries(summary.statusCounts).map(([status, count]) => {
                    let color = "#64748b";
                    if (status.toLowerCase().includes("closed") || status.toLowerCase().includes("done") || status.toLowerCase().includes("resolved")) color = "#10b981";
                    else if (status.toLowerCase().includes("active") || status.toLowerCase().includes("progress")) color = "#2563eb";
                    else if (status.toLowerCase().includes("new")) color = "#0284c7";
                    return { label: status, count, color };
                  })}
                />
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }}>
                {/* Status Breakdown Gradient Bars */}
                <h4 style={s.sectionSubtitle}>Status Breakdown</h4>
                {summary?.statusCounts && Object.keys(summary.statusCounts).length > 0 ? (
                  Object.entries(summary.statusCounts).map(([status, count]) => {
                    const pct = summary.totalCount > 0 ? Math.round((count / summary.totalCount) * 100) : 0;
                    let grad = "linear-gradient(90deg, #64748b 0%, #94a3b8 100%)";
                    if (status.toLowerCase().includes("closed") || status.toLowerCase().includes("done") || status.toLowerCase().includes("resolved")) grad = "linear-gradient(90deg, #10b981 0%, #34d399 100%)";
                    else if (status.toLowerCase().includes("active") || status.toLowerCase().includes("progress")) grad = "linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)";
                    else if (status.toLowerCase().includes("new")) grad = "linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)";

                    return (
                      <div key={status} style={s.barRow}>
                        <div style={s.barLabelContainer}>
                          <span style={s.barLabel}>{status}</span>
                          <span style={s.barVal}>{count} ({pct}%)</span>
                        </div>
                        <div style={s.barTrack}>
                          <div style={{ ...s.barFill, width: `${pct}%`, background: grad, borderRadius: "6px" }} />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={s.noDataMsg}>No status breakdown data available.</div>
                )}

                {/* Status Distribution Summary & Health Highlights (Fills bottom gap) */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  marginTop: "16px",
                  padding: "12px 14px",
                  background: "#f8fafc",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Primary Status</span>
                    <span style={{ fontSize: "13px", fontWeight: "800", color: "#0284c7" }}>
                      New / Backlog
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Completion Status</span>
                    <span style={{ fontSize: "13px", fontWeight: "800", color: summary?.completedCount > 0 ? "#10b981" : "#64748b" }}>
                      {summary?.completedCount || 0} Resolved
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT COLUMN: Velocity & Completion Trend SVG Graph */}
        <div style={s.columnCard}>
          <div style={s.cardHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <TrendingUp size={18} color="#10b981" />
              <h3 style={s.cardTitle}>Velocity & Completion Trend Graph</h3>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "11px", fontWeight: "700" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#2563eb" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#2563eb" }} /> Created
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#10b981" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#10b981" }} /> Completed
              </span>
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            {loading && !summary ? (
              <div style={{ padding: "20px", display: "flex", alignItems: "flex-end", gap: "16px", height: "180px" }}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={`vel-skel-${i}`} style={{ ...s.skeletonBar, flex: 1, height: `${25 + (i * 12) % 65}%` }} />
                ))}
              </div>
            ) : (
              <SVGVelocityChart data={summary?.velocityTrend || []} />
            )}
          </div>
        </div>
      </div>
      )}

      {/* SECTION 2: WORK ITEM TYPES GRAPH CARD (temporarily hidden) */}
      {false && activeSection === "types" && (
      <div key={`section-types-${activeSection}-${slideDirection}`} className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"} style={s.columnCard}>
        <div style={s.cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag size={18} color="#2563eb" />
            <h3 style={s.cardTitle}>Work Item Types — Multi-Line Trend Graph</h3>
          </div>
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
            {summary?.totalCount || 0} Total Items · {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} Breakdown
          </span>
        </div>

        {loading && !summary ? (
          <div style={{ padding: "20px", display: "flex", alignItems: "flex-end", gap: "12px", height: "180px" }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={`type-skel-${i}`} style={{ ...s.skeletonBar, flex: 1, height: `${20 + (i * 15) % 70}%` }} />
            ))}
          </div>
        ) : (
          <>
            {/* Stacked Proportional Distribution Bar — color-consistent with line chart */}
            {summary?.typeCounts && Object.keys(summary.typeCounts).length > 0 && (
              <div style={{ marginTop: "16px", marginBottom: "4px" }}>
                <div style={{ display: "flex", height: "10px", borderRadius: "6px", overflow: "hidden", background: "#f1f5f9" }}>
                  {Object.entries(summary.typeCounts).map(([type, count]) => {
                    const pct = summary.totalCount > 0 ? (count / summary.totalCount) * 100 : 0;
                    const bg = getTypeColor(type);
                    return pct > 0 ? (
                      <div
                        key={`stacked-${type}`}
                        style={{ width: `${pct}%`, background: bg, height: "100%" }}
                        title={`${type}: ${count} (${Math.round(pct)}%)`}
                      />
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Multi-Line SVG Trend Graph — one colored line per type */}
            <SVGWorkItemTypeLineChart
              velocityTrend={summary?.velocityTrend || []}
              typeCounts={summary?.typeCounts || {}}
            />
          </>
        )}
      </div>
      )}

      {/* SECTION 3: USER PERFORMANCE METRICS LEADERBOARD */}
      {activeSection === "leaderboard" && (
      <div key={`section-leaderboard-${activeSection}-${slideDirection}`} className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"} style={s.columnCard}>
        <div style={s.cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Users size={18} color="#8b5cf6" />
            <h3 style={s.cardTitle}>
              {selectedUserFilter !== "all"
                ? `User Performance Breakdown: ${selectedUserFilter}`
                : "User Performance Metrics & Team Leaderboard"}
            </h3>
          </div>
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
            {selectedUserFilter !== "all"
              ? "Individual Performance Summary"
              : `${leaderboard.length} Team Members Tracked`}
          </span>
        </div>

        <div style={{ overflowX: "auto", marginTop: "16px", position: "relative" }}>
          {loading && leaderboard.length > 0 && (
            <div style={s.overlayLoader}>
              <RefreshCw size={20} color="#8b5cf6" style={{ animation: "spin 0.8s linear infinite" }} />
              <span>Updating team metrics…</span>
            </div>
          )}
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Assignee</th>
                <th style={s.thCenter}>Total Assigned</th>
                <th style={s.thCenter}>Completed</th>
                <th style={s.thCenter}>Active</th>
                <th style={s.thCenter}>Overdue</th>
                <th style={s.th}>Completion Rate</th>
                <th style={s.thCenter}>Avg Speed (Days)</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.length === 0 && loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`lb-skel-${i}`} style={s.tr}>
                    <td style={s.td}><div style={s.skeletonBar} /></td>
                    <td style={s.tdCenter}><div style={{ ...s.skeletonBar, margin: "0 auto", width: "40px" }} /></td>
                    <td style={s.tdCenter}><div style={{ ...s.skeletonBar, margin: "0 auto", width: "30px" }} /></td>
                    <td style={s.tdCenter}><div style={{ ...s.skeletonBar, margin: "0 auto", width: "30px" }} /></td>
                    <td style={s.tdCenter}><div style={{ ...s.skeletonBar, margin: "0 auto", width: "30px" }} /></td>
                    <td style={s.td}><div style={{ ...s.skeletonBar, width: "100%" }} /></td>
                    <td style={s.tdCenter}><div style={{ ...s.skeletonBar, margin: "0 auto", width: "50px" }} /></td>
                  </tr>
                ))
              ) : leaderboard.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: "24px", color: "#94a3b8", fontStyle: "italic" }}>
                    No user performance data recorded.
                  </td>
                </tr>
              ) : (
                leaderboard
                  .slice((leaderboardPage - 1) * leaderboardPageSize, leaderboardPage * leaderboardPageSize)
                  .map((user, idx) => {
                    const actualIdx = (leaderboardPage - 1) * leaderboardPageSize + idx;
                    const sharePct = user.teamWorkloadShare !== undefined ? user.teamWorkloadShare : 0;
                    const rankNum = user.teamRank || (actualIdx + 1);
                    const isFilteredUser = selectedUserFilter !== "all";

                    const rankBadge = rankNum === 1 ? "🥇 #1" : rankNum === 2 ? "🥈 #2" : rankNum === 3 ? "🥉 #3" : `#${rankNum}`;
                    const badgeBg = rankNum === 1 ? "#fef3c7" : rankNum === 2 ? "#f1f5f9" : rankNum === 3 ? "#ffedd5" : "#f1f5f9";
                    const badgeColor = rankNum === 1 ? "#d97706" : rankNum === 2 ? "#475569" : rankNum === 3 ? "#c2410c" : "#64748b";

                    return (
                      <tr
                        key={user.name || idx}
                        onClick={() => {
                          setSelectedUser(user);
                          setUserTab("all");
                          setUserSearchText("");
                        }}
                        style={{
                          ...s.tr,
                          cursor: "pointer",
                          transition: "background 0.15s ease"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#ffffff"}
                        title={`Click to open in-depth user tracker for ${user.name}`}
                      >
                        <td style={s.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            {!isFilteredUser && (
                              <span style={{ fontSize: "11px", fontWeight: "800", padding: "3px 8px", borderRadius: "5px", background: badgeBg, color: badgeColor }}>
                                {rankBadge}
                              </span>
                            )}
                            <div style={s.avatarBox}>
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              <span style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>{user.name}</span>
                              {user.email && <span style={{ fontSize: "11px", color: "#64748b" }}>{user.email}</span>}
                            </div>
                          </div>
                        </td>
                        <td style={s.tdCenterBold}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <span>{user.totalAssigned}</span>
                            <span style={{ fontSize: "10px", color: "#64748b", fontWeight: "600" }}>
                              {isFilteredUser ? "Work Items Assigned" : `${sharePct}% Team Workload`}
                            </span>
                          </div>
                        </td>
                        <td style={{ ...s.tdCenterBold, color: "#16a34a" }}>{user.completed}</td>
                        <td style={{ ...s.tdCenterBold, color: "#2563eb" }}>{user.active}</td>
                        <td style={{ ...s.tdCenterBold, color: user.overdue > 0 ? "#dc2626" : "#64748b" }}>
                          {user.overdue}
                        </td>
                        <td style={s.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <SVGRadialGauge percent={user.completionRate} size={36} strokeWidth={3.5} color={user.completionRate >= 80 ? "#16a34a" : THEME} />
                            <div style={{ flex: 1, height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${user.completionRate}%`, height: "100%", background: user.completionRate >= 80 ? "#16a34a" : THEME }} />
                            </div>
                          </div>
                        </td>
                        <td style={s.tdCenterBold}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                            <span>{user.avgResolutionDays} d</span>
                            <span style={{ fontSize: "10px", fontWeight: "700", padding: "1px 5px", borderRadius: "3px", background: "#f0fdf4", color: "#166534" }}>⚡ SLA</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION CONTROLS FOR LEADERBOARD WITH SMART WINDOWING & GO TO PAGE */}
        {Math.ceil((leaderboard.length || 0) / leaderboardPageSize) > 1 && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: "14px",
            marginTop: "12px",
            borderTop: "1px solid #f1f5f9",
            flexWrap: "wrap",
            gap: "12px"
          }}>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              Showing <strong>{(leaderboardPage - 1) * leaderboardPageSize + 1}</strong> to{" "}
              <strong>{Math.min(leaderboardPage * leaderboardPageSize, leaderboard.length)}</strong> of{" "}
              <strong>{leaderboard.length}</strong> team members
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <button
                  type="button"
                  onClick={() => setLeaderboardPage((prev) => Math.max(1, prev - 1))}
                  disabled={leaderboardPage <= 1}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: "600",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: leaderboardPage <= 1 ? "#f8fafc" : "#ffffff",
                    color: leaderboardPage <= 1 ? "#94a3b8" : "#334155",
                    cursor: leaderboardPage <= 1 ? "not-allowed" : "pointer",
                    outline: "none"
                  }}
                >
                  Previous
                </button>

                {/* SMART WINDOWED PAGE NUMBERS */}
                {(() => {
                  const totalP = Math.ceil(leaderboard.length / leaderboardPageSize);
                  const pages = [];
                  if (totalP <= 7) {
                    for (let i = 1; i <= totalP; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (leaderboardPage > 4) pages.push("...");
                    const start = Math.max(2, leaderboardPage - 1);
                    const end = Math.min(totalP - 1, leaderboardPage + 1);
                    for (let i = start; i <= end; i++) {
                      if (!pages.includes(i)) pages.push(i);
                    }
                    if (leaderboardPage < totalP - 3) pages.push("...");
                    if (!pages.includes(totalP)) pages.push(totalP);
                  }

                  return pages.map((item, idx) => {
                    if (item === "...") {
                      return (
                        <span key={`lb-ellipsis-${idx}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: "13px", fontWeight: "700" }}>
                          …
                        </span>
                      );
                    }
                    const isCurrent = item === leaderboardPage;
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setLeaderboardPage(item)}
                        style={{
                          minWidth: "32px",
                          height: "32px",
                          padding: "0 6px",
                          fontSize: "12px",
                          fontWeight: isCurrent ? "700" : "500",
                          borderRadius: "6px",
                          border: isCurrent ? "1px solid #8b5cf6" : "1px solid #e2e8f0",
                          background: isCurrent ? "#8b5cf6" : "#ffffff",
                          color: isCurrent ? "#ffffff" : "#475569",
                          cursor: "pointer",
                          outline: "none"
                        }}
                      >
                        {item}
                      </button>
                    );
                  });
                })()}

                <button
                  type="button"
                  onClick={() => setLeaderboardPage((prev) => Math.min(Math.ceil(leaderboard.length / leaderboardPageSize), prev + 1))}
                  disabled={leaderboardPage >= Math.ceil(leaderboard.length / leaderboardPageSize)}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: "600",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: leaderboardPage >= Math.ceil(leaderboard.length / leaderboardPageSize) ? "#f8fafc" : "#ffffff",
                    color: leaderboardPage >= Math.ceil(leaderboard.length / leaderboardPageSize) ? "#94a3b8" : "#334155",
                    cursor: leaderboardPage >= Math.ceil(leaderboard.length / leaderboardPageSize) ? "not-allowed" : "pointer",
                    outline: "none"
                  }}
                >
                  Next
                </button>
              </div>

              {/* GO TO PAGE FEATURE */}
              <div style={{ display: "flex", alignItems: "center", gap: "5px", marginLeft: "6px", borderLeft: "1px solid #e2e8f0", paddingLeft: "8px" }}>
                <span style={{ fontSize: "12px", color: "#64748b" }}>Go to:</span>
                <input
                  type="number"
                  min={1}
                  max={Math.ceil(leaderboard.length / leaderboardPageSize)}
                  placeholder={String(leaderboardPage)}
                  value={leaderboardGotoInput}
                  onChange={(e) => setLeaderboardGotoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const targetP = parseInt(leaderboardGotoInput, 10);
                      const totalP = Math.ceil(leaderboard.length / leaderboardPageSize);
                      if (targetP >= 1 && targetP <= totalP) {
                        setLeaderboardPage(targetP);
                        setLeaderboardGotoInput("");
                      }
                    }
                  }}
                  style={{
                    width: "48px",
                    height: "30px",
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: "600",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    outline: "none"
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const targetP = parseInt(leaderboardGotoInput, 10);
                    const totalP = Math.ceil(leaderboard.length / leaderboardPageSize);
                    if (targetP >= 1 && targetP <= totalP) {
                      setLeaderboardPage(targetP);
                      setLeaderboardGotoInput("");
                    }
                  }}
                  style={{
                    height: "30px",
                    padding: "0 10px",
                    fontSize: "12px",
                    fontWeight: "600",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    color: "#334155",
                    cursor: "pointer",
                    outline: "none"
                  }}
                >
                  Go
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* SECTION 4: ETA MISSED vs ON-TIME & OVERDUE TRACKER */}
      {activeSection === "eta" && (
      <div key={`section-eta-${activeSection}-${slideDirection}`} className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"}>
      <div style={{ ...s.columnCard, marginTop: activeSection === "all" ? "24px" : "0" }}>
        <div style={s.cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <TrendingUp size={18} color="#2563eb" />
            <h3 style={s.cardTitle}>ETA Compliance Trend</h3>
          </div>
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
            {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} · Missed vs On-Time
          </span>
        </div>
        <div style={{ marginTop: "16px" }}>
          {loading && !summary ? (
            <div style={{ padding: "20px", display: "flex", alignItems: "flex-end", gap: "16px", height: "180px" }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={`eta-tr-skel-${i}`} style={{ ...s.skeletonBar, flex: 1, height: `${30 + (i * 10) % 60}%` }} />
              ))}
            </div>
          ) : (
            <SVGEtaTrendChart etaTrend={summary?.etaTrend || []} />
          )}
        </div>
      </div>

      {/* ETA TRACKER & OVERDUE ITEMS LIST CARD WITH PAGINATION */}
      <div style={{ ...s.columnCard, marginTop: "24px", marginBottom: "40px" }}>
        <div style={s.cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertTriangle size={18} color="#dc2626" />
            <h3 style={s.cardTitle}>ETA &amp; SLA Overdue Tracker</h3>
          </div>
          <span style={{ fontSize: "12px", color: "#dc2626", fontWeight: "700", background: "#fee2e2", padding: "4px 10px", borderRadius: "6px" }}>
            {etaData?.overdueCount || 0} Action Required
          </span>
        </div>

        <div style={{ overflowX: "auto", marginTop: "16px", position: "relative" }}>
          {loading && etaData?.overdueItems && etaData.overdueItems.length > 0 && (
            <div style={s.overlayLoader}>
              <RefreshCw size={20} color="#dc2626" style={{ animation: "spin 0.8s linear infinite" }} />
              <span>Updating overdue list…</span>
            </div>
          )}

          {loading && (!etaData?.overdueItems || etaData.overdueItems.length === 0) ? (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Work Item ID</th>
                  <th style={s.th}>Title</th>
                  <th style={s.th}>Type</th>
                  <th style={s.th}>Assignee</th>
                  <th style={s.thCenter}>Target Date</th>
                  <th style={s.thCenter}>Days Overdue</th>
                  <th style={s.thRight}>Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`eta-skel-${i}`} style={s.tr}>
                    <td style={s.tdBold}><div style={{ ...s.skeletonBar, width: "50px" }} /></td>
                    <td style={s.td}><div style={{ ...s.skeletonBar, width: "160px" }} /></td>
                    <td style={s.td}><div style={{ ...s.skeletonBar, width: "50px" }} /></td>
                    <td style={s.td}><div style={{ ...s.skeletonBar, width: "100px" }} /></td>
                    <td style={s.tdCenter}><div style={{ ...s.skeletonBar, margin: "0 auto", width: "70px" }} /></td>
                    <td style={s.tdCenter}><div style={{ ...s.skeletonBar, margin: "0 auto", width: "60px" }} /></td>
                    <td style={s.tdRight}><div style={{ ...s.skeletonBar, marginLeft: "auto", width: "70px" }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : !etaData?.overdueItems || etaData.overdueItems.length === 0 ? (
            <div style={s.emptyBoxGood}>
              <CheckCircle2 size={24} color="#16a34a" />
              <span>Great job! No work items are currently overdue past their target date.</span>
            </div>
          ) : (
            <>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Work Item ID</th>
                    <th style={s.th}>Title</th>
                    <th style={s.th}>Type</th>
                    <th style={s.th}>Assignee</th>
                    <th style={s.thCenter}>Target Date</th>
                    <th style={s.thCenter}>Days Overdue</th>
                    <th style={s.thRight}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {etaData.overdueItems
                    .slice((etaPage - 1) * etaPageSize, etaPage * etaPageSize)
                    .map((item) => (
                      <tr key={item.id} style={s.tr}>
                        <td style={s.tdBold}>#{item.id}</td>
                        <td style={{ ...s.td, maxWidth: "220px" }}>
                          <span
                            title={item.title}
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#0f172a",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "220px"
                            }}
                          >
                            {item.title}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={{
                            fontSize: "11px",
                            fontWeight: "700",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            background: item.type?.toLowerCase().includes("bug") ? "#fee2e2" : "#dbeafe",
                            color: item.type?.toLowerCase().includes("bug") ? "#dc2626" : "#2563eb"
                          }}>
                            {item.type}
                          </span>
                        </td>
                        <td style={s.td}>{item.assignedTo}</td>
                        <td style={s.tdCenterBold}>{item.targetDate || "N/A"}</td>
                        <td style={s.tdCenter}>
                          <span style={{ fontSize: "12px", fontWeight: "800", color: "#dc2626", background: "#fee2e2", padding: "2px 8px", borderRadius: "4px" }}>
                            +{item.daysOverdue} Days
                          </span>
                        </td>
                        <td style={s.tdRight}>
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            style={s.linkBtn}
                          >
                            Open ADO <ExternalLink size={12} />
                          </a>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* PAGINATION CONTROLS FOR OVERDUE ITEMS WITH SMART WINDOWING & GO TO PAGE */}
              {Math.ceil((etaData.overdueItems.length || 0) / etaPageSize) > 1 && (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: "14px",
                  marginTop: "12px",
                  borderTop: "1px solid #f1f5f9",
                  flexWrap: "wrap",
                  gap: "12px"
                }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    Showing <strong>{(etaPage - 1) * etaPageSize + 1}</strong> to{" "}
                    <strong>{Math.min(etaPage * etaPageSize, etaData.overdueItems.length)}</strong> of{" "}
                    <strong>{etaData.overdueItems.length}</strong> overdue items
                  </span>

                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <button
                        type="button"
                        onClick={() => setEtaPage((prev) => Math.max(1, prev - 1))}
                        disabled={etaPage <= 1}
                        style={{
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          background: etaPage <= 1 ? "#f8fafc" : "#ffffff",
                          color: etaPage <= 1 ? "#94a3b8" : "#334155",
                          cursor: etaPage <= 1 ? "not-allowed" : "pointer",
                          outline: "none"
                        }}
                      >
                        Previous
                      </button>

                      {/* SMART WINDOWED PAGE NUMBERS */}
                      {(() => {
                        const totalP = Math.ceil(etaData.overdueItems.length / etaPageSize);
                        const pages = [];
                        if (totalP <= 7) {
                          for (let i = 1; i <= totalP; i++) pages.push(i);
                        } else {
                          pages.push(1);
                          if (etaPage > 4) pages.push("...");
                          const start = Math.max(2, etaPage - 1);
                          const end = Math.min(totalP - 1, etaPage + 1);
                          for (let i = start; i <= end; i++) {
                            if (!pages.includes(i)) pages.push(i);
                          }
                          if (etaPage < totalP - 3) pages.push("...");
                          if (!pages.includes(totalP)) pages.push(totalP);
                        }

                        return pages.map((item, idx) => {
                          if (item === "...") {
                            return (
                              <span key={`eta-ellipsis-${idx}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: "13px", fontWeight: "700" }}>
                                …
                              </span>
                            );
                          }
                          const isCurrent = item === etaPage;
                          return (
                            <button
                              key={item}
                              type="button"
                              onClick={() => setEtaPage(item)}
                              style={{
                                minWidth: "32px",
                                height: "32px",
                                padding: "0 6px",
                                fontSize: "12px",
                                fontWeight: isCurrent ? "700" : "500",
                                borderRadius: "6px",
                                border: isCurrent ? "1px solid #2563eb" : "1px solid #e2e8f0",
                                background: isCurrent ? "#2563eb" : "#ffffff",
                                color: isCurrent ? "#ffffff" : "#475569",
                                cursor: "pointer",
                                outline: "none"
                              }}
                            >
                              {item}
                            </button>
                          );
                        });
                      })()}

                      <button
                        type="button"
                        onClick={() => setEtaPage((prev) => Math.min(Math.ceil(etaData.overdueItems.length / etaPageSize), prev + 1))}
                        disabled={etaPage >= Math.ceil(etaData.overdueItems.length / etaPageSize)}
                        style={{
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          background: etaPage >= Math.ceil(etaData.overdueItems.length / etaPageSize) ? "#f8fafc" : "#ffffff",
                          color: etaPage >= Math.ceil(etaData.overdueItems.length / etaPageSize) ? "#94a3b8" : "#334155",
                          cursor: etaPage >= Math.ceil(etaData.overdueItems.length / etaPageSize) ? "not-allowed" : "pointer",
                          outline: "none"
                        }}
                      >
                        Next
                      </button>
                    </div>

                    {/* GO TO PAGE FEATURE */}
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", marginLeft: "6px", borderLeft: "1px solid #e2e8f0", paddingLeft: "8px" }}>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Go to:</span>
                      <input
                        type="number"
                        min={1}
                        max={Math.ceil(etaData.overdueItems.length / etaPageSize)}
                        placeholder={String(etaPage)}
                        value={etaGotoInput}
                        onChange={(e) => setEtaGotoInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const targetP = parseInt(etaGotoInput, 10);
                            const totalP = Math.ceil(etaData.overdueItems.length / etaPageSize);
                            if (targetP >= 1 && targetP <= totalP) {
                              setEtaPage(targetP);
                              setEtaGotoInput("");
                            }
                          }
                        }}
                        style={{
                          width: "48px",
                          height: "30px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "600",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          outline: "none"
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const targetP = parseInt(etaGotoInput, 10);
                          const totalP = Math.ceil(etaData.overdueItems.length / etaPageSize);
                          if (targetP >= 1 && targetP <= totalP) {
                            setEtaPage(targetP);
                            setEtaGotoInput("");
                          }
                        }}
                        style={{
                          height: "30px",
                          padding: "0 10px",
                          fontSize: "12px",
                          fontWeight: "600",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          background: "#f8fafc",
                          color: "#334155",
                          cursor: "pointer",
                          outline: "none"
                        }}
                      >
                        Go
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
      )}

        </AnalyticsSectionShell>
      )}

      {activeSection === "user-activity" && (
        <div
          key={`section-user-activity-${slideDirection}`}
          className={slideDirection === "right" ? "analytics-slide-right" : "analytics-slide-left"}
        >
          <PerformanceActivityPage embedMode title="User Activity" />
      </div>
      )}

      {/* IN-DEPTH USER TRACKER MODAL / SLIDE-OVER DRAWER */}
      {selectedUser && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "flex-end"
        }}>
          <div style={{
            width: "90%",
            maxWidth: "850px",
            height: "100vh",
            background: "#ffffff",
            boxShadow: "-10px 0 30px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}>
            {/* Modal Header */}
            <div style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              background: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: THEME,
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  fontWeight: "800"
                }}>
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: "#0f172a" }}>
                    {selectedUser.name}
                  </h2>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    {selectedUser.email || "Team Member"} • In-Depth User Tracker
                  </span>
                </div>
              </div>

              <button
                onClick={() => setSelectedUser(null)}
                style={{
                  border: "none",
                  background: "#e2e8f0",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#475569",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                ✕
              </button>
            </div>

            {/* User Metrics Summary Strip */}
            <div style={{
              padding: "16px 24px",
              background: "#ffffff",
              borderBottom: "1px solid #e2e8f0",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
              gap: "12px"
            }}>
              <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Assigned</span>
                <div style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a" }}>{selectedUser.totalAssigned}</div>
              </div>
              <div style={{ background: "#f0fdf4", padding: "10px", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
                <span style={{ fontSize: "10px", fontWeight: "700", color: "#166534", textTransform: "uppercase" }}>Completed</span>
                <div style={{ fontSize: "18px", fontWeight: "800", color: "#16a34a" }}>{selectedUser.completed}</div>
              </div>
              <div style={{ background: "#eff6ff", padding: "10px", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
                <span style={{ fontSize: "10px", fontWeight: "700", color: "#1e40af", textTransform: "uppercase" }}>Active</span>
                <div style={{ fontSize: "18px", fontWeight: "800", color: "#2563eb" }}>{selectedUser.active}</div>
              </div>
              <div style={{ background: selectedUser.overdue > 0 ? "#fef2f2" : "#f8fafc", padding: "10px", borderRadius: "8px", border: selectedUser.overdue > 0 ? "1px solid #fecaca" : "1px solid #f1f5f9" }}>
                <span style={{ fontSize: "10px", fontWeight: "700", color: selectedUser.overdue > 0 ? "#dc2626" : "#64748b", textTransform: "uppercase" }}>Overdue</span>
                <div style={{ fontSize: "18px", fontWeight: "800", color: selectedUser.overdue > 0 ? "#dc2626" : "#64748b" }}>{selectedUser.overdue}</div>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Completion Rate</span>
                <div style={{ fontSize: "18px", fontWeight: "800", color: THEME }}>{selectedUser.completionRate}%</div>
              </div>
            </div>

            {/* Filter Tabs & Search Bar */}
            <div style={{
              padding: "12px 24px",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap"
            }}>
              <div style={{ display: "flex", gap: "6px" }}>
                {[
                  { key: "all", label: `All (${userWorkItems.length})` },
                  { key: "active", label: `Active (${userWorkItems.filter(i => !i.isCompleted).length})` },
                  { key: "overdue", label: `Overdue (${userWorkItems.filter(i => i.isOverdue).length})` },
                  { key: "completed", label: `Completed (${userWorkItems.filter(i => i.isCompleted).length})` }
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setUserTab(t.key)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: userTab === t.key ? "700" : "600",
                      border: "none",
                      background: userTab === t.key ? THEME : "#ffffff",
                      color: userTab === t.key ? "#ffffff" : "#64748b",
                      cursor: "pointer",
                      boxShadow: userTab === t.key ? "0 2px 4px rgba(37,99,235,0.2)" : "0 1px 2px rgba(0,0,0,0.04)"
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <input
                type="text"
                placeholder="Search work items by ID or title..."
                value={userSearchText}
                onChange={(e) => setUserSearchText(e.target.value)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "12px",
                  outline: "none",
                  width: "220px"
                }}
              />
            </div>

            {/* Work Items Content List */}
            <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto" }}>
              {userLoading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px", gap: "10px", color: "#64748b" }}>
                  <div className="animate-spin" style={{ width: "20px", height: "20px", border: "2px solid #cbd5e1", borderTopColor: THEME, borderRadius: "50%" }} />
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>Fetching detailed work items for {selectedUser.name}...</span>
                </div>
              ) : (() => {
                const filtered = userWorkItems.filter((i) => {
                  if (userTab === "active" && i.isCompleted) return false;
                  if (userTab === "overdue" && !i.isOverdue) return false;
                  if (userTab === "completed" && !i.isCompleted) return false;
                  if (userSearchText.trim()) {
                    const txt = userSearchText.toLowerCase();
                    return String(i.id).includes(txt) || (i.title || "").toLowerCase().includes(txt);
                  }
                  return true;
                });

                if (filtered.length === 0) {
                  return (
                    <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8", fontStyle: "italic", fontSize: "13px" }}>
                      No work items match the selected filter.
                    </div>
                  );
                }

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {filtered.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          padding: "14px 18px",
                          border: item.isOverdue ? "1px solid #fecaca" : "1px solid #e2e8f0",
                          borderRadius: "10px",
                          background: item.isOverdue ? "#fff5f5" : "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "12px", fontWeight: "800", color: "#0f172a" }}>#{item.id}</span>
                            <span style={{
                              fontSize: "10px",
                              fontWeight: "700",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              background: item.type?.toLowerCase().includes("bug") ? "#fee2e2" : item.type?.toLowerCase().includes("task") ? "#dbeafe" : "#dcfce7",
                              color: item.type?.toLowerCase().includes("bug") ? "#dc2626" : item.type?.toLowerCase().includes("task") ? "#2563eb" : "#16a34a"
                            }}>
                              {item.type}
                            </span>
                            <span style={{
                              fontSize: "10px",
                              fontWeight: "600",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              background: "#f1f5f9",
                              color: "#475569"
                            }}>
                              {item.state}
                            </span>
                            {item.isOverdue && (
                              <span style={{ fontSize: "10px", fontWeight: "800", color: "#dc2626", background: "#fee2e2", padding: "2px 6px", borderRadius: "4px" }}>
                                ⚠️ +{item.daysOverdue} Days Overdue
                              </span>
                            )}
                          </div>

                          <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "520px" }}>
                            {item.title}
                          </span>

                          <div style={{ fontSize: "11px", color: "#94a3b8", display: "flex", gap: "12px", marginTop: "2px" }}>
                            <span>Created: {item.createdDate || "N/A"}</span>
                            {item.targetDate && <span>Target: {item.targetDate}</span>}
                            {item.closedDate && <span style={{ color: "#16a34a" }}>Closed: {item.closedDate}</span>}
                          </div>
                        </div>

                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "6px 12px",
                            background: "rgba(37, 99, 235, 0.08)",
                            color: THEME,
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "700",
                            textDecoration: "none",
                            flexShrink: 0
                          }}
                        >
                          Open in ADO <ExternalLink size={12} />
                        </a>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: {
    padding: "clamp(16px, 4vw, 24px)",
    minHeight: "100vh",
    boxSizing: "border-box",
    background: "#f8fafc"
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: THEME,
    cursor: "pointer",
    fontWeight: "700",
    background: "none",
    border: "none",
    padding: 0,
    marginBottom: "16px"
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "16px"
  },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "4px"
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "800",
    color: "#0f172a"
  },
  sub: {
    margin: "4px 0 0 0",
    fontSize: "14px",
    color: "#64748b"
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    background: THEME,
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)"
  },
  filterCard: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    padding: "14px 18px",
    background: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "14px"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap"
  },
  filterLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase"
  },
  selectWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "#f1f5f9",
    padding: "4px 10px",
    borderRadius: "6px"
  },
  selectLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b"
  },
  selectInput: {
    background: "transparent",
    border: "none",
    fontSize: "12px",
    fontWeight: "700",
    color: "#0f172a",
    outline: "none",
    cursor: "pointer"
  },
  timeframeGroup: {
    display: "flex",
    background: "#f1f5f9",
    padding: "3px",
    borderRadius: "8px",
    gap: "2px"
  },
  timeframeBtn: {
    padding: "5px 14px",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.15s ease"
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 16px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#dc2626",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "20px"
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "24px"
  },
  kpiCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "18px",
    boxShadow: "0 1px 3px rgba(15,23,42,0.04)"
  },
  kpiHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px"
  },
  kpiTitle: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.03em"
  },
  kpiIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "8px"
  },
  kpiValue: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#0f172a",
    marginBottom: "6px"
  },
  kpiFooter: {
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "10px"
  },
  progressBarTrack: {
    width: "100%",
    height: "6px",
    background: "#f1f5f9",
    borderRadius: "3px",
    overflow: "hidden"
  },
  progressBarFill: {
    height: "100%",
    borderRadius: "3px",
    transition: "width 0.3s ease"
  },
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
    gap: "20px"
  },
  columnCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(15,23,42,0.04)"
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px"
  },
  cardTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: "700",
    color: "#0f172a"
  },
  badgeLabel: {
    fontSize: "10px",
    fontWeight: "800",
    background: "#f1f5f9",
    color: "#64748b",
    padding: "3px 8px",
    borderRadius: "4px"
  },
  sectionSubtitle: {
    margin: 0,
    fontSize: "12px",
    fontWeight: "700",
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  barRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  barLabelContainer: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "12px"
  },
  barLabel: {
    fontWeight: "600",
    color: "#334155"
  },
  barVal: {
    fontWeight: "700",
    color: "#64748b"
  },
  barTrack: {
    height: "8px",
    background: "#f1f5f9",
    borderRadius: "4px",
    overflow: "hidden"
  },
  barFill: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.3s ease"
  },
  noDataMsg: {
    padding: "20px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "13px",
    fontStyle: "italic"
  },
  avatarBox: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: THEME,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: "700"
  },
  table: {
    width: "100%",
    minWidth: "680px",
    borderCollapse: "collapse",
    fontSize: "13px"
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: "700",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap"
  },
  thCenter: {
    textAlign: "center",
    padding: "10px 12px",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: "700",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap"
  },
  thRight: {
    textAlign: "right",
    padding: "10px 12px",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: "700",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap"
  },
  tr: {
    borderBottom: "1px solid #f1f5f9"
  },
  td: {
    padding: "12px",
    whiteSpace: "nowrap"
  },
  tdBold: {
    padding: "12px",
    fontWeight: "700",
    color: "#0f172a",
    whiteSpace: "nowrap"
  },
  tdCenter: {
    padding: "12px",
    textAlign: "center",
    whiteSpace: "nowrap"
  },
  tdCenterBold: {
    padding: "12px",
    textAlign: "center",
    fontWeight: "700",
    color: "#0f172a",
    whiteSpace: "nowrap"
  },
  tdRight: {
    padding: "12px",
    textAlign: "right",
    whiteSpace: "nowrap"
  },
  emptyBoxGood: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "20px",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "10px",
    color: "#166534",
    fontSize: "13px",
    fontWeight: "600"
  },
  linkBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: "700",
    color: THEME,
    textDecoration: "none",
    background: "rgba(37, 99, 235, 0.08)",
    padding: "4px 10px",
    borderRadius: "6px"
  },
  skeletonBar: {
    height: "14px",
    background: "#e2e8f0",
    borderRadius: "4px",
    width: "100px",
    animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  overlayLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(2px)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: THEME,
    fontWeight: "600",
    fontSize: "13px",
    borderRadius: "8px",
  }
};
